import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Trash, FileText, 
  MagnifyingGlass, Package, X, Check, Image as ImageIcon, CloudArrowUp, NotePencil, PencilSimple, FloppyDisk, ClockCounterClockwise, ArrowUp, ArrowDown, BookOpen, CalendarBlank, Warning, ImageSquare, Folder, FilePdf, Eye, MagnifyingGlassPlus, MagnifyingGlassMinus, EnvelopeSimple, PaperPlaneTilt, CalendarCheck, ChatText, ChartLineUp, Percent, TrendUp, TrendDown, Phone, MapPin, Buildings, Globe, InstagramLogo, DotsSixVertical, Truck, Scales, Lightbulb, Flag
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import OptimizedImage, { optimizeCloudinaryUrl } from "@/components/OptimizedImage";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { RappelClientCard } from "@/pages/RappelPage";
import { EncomendaOrdersTable, formatEncomendaNum } from "@/components/EncomendaOrdersTable";
import { cleanProductName, toSentenceCase } from "@/lib/textFormat";
import { getApiErrorMessage } from "@/lib/apiError";
import { useSettings } from "@/contexts/SettingsContext";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PANEL_SIZES = ["1920x1005", "2400x1005", "2400x1200", "1200x1200"];

// Helper to resolve image URLs (handles both /api/files/xxx and full URLs)
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Product code type priority for sorting
const TYPE_PRIORITY = {
  // 1º - Más importante (agrupados)
  'GRPT': 1,
  'GRPIC': 1,
  'GRLPT': 1,
  'GRLPIC': 1,
  // 2º
  'GRPFXL': 2,
  // 3º
  'GRDK': 3,
  // 4º
  'GRF': 4,
  'GRFXL': 4,
  // 5º
  'GRXL': 5,
  // Otros
  'GRBS': 6,
  'GRDV': 6,
  'GREX': 6,
  'GEX': 6,
  'GRPM': 6
};

// Extract type code from product code
const getTypeFromCode = (code) => {
  if (!code) return null;
  const match = code.match(/(GRPFXL|GRLPIC|GRFXL|GRPIC|GRLPT|GRPT|GRDK|GRXL|GRBS|GRDV|GREX|GRPM|GRF|GEX)/i);
  return match ? match[1].toUpperCase() : null;
};

// Get priority for a product
const getProductPriority = (code) => {
  const typeCode = getTypeFromCode(code);
  return typeCode ? (TYPE_PRIORITY[typeCode] || 99) : 99;
};

// Sort showroom products: manual drag & drop order (display_order) takes priority; items
// without one yet fall back to the automatic priority-by-product-type sort, so newly added
// products still land in a sensible spot until the admin drags them into place.
const sortShowroomProducts = (products) => {
  return [...products].sort((a, b) => {
    const orderA = a.display_order;
    const orderB = b.display_order;
    if (orderA != null && orderB != null) return orderA - orderB;
    if (orderA != null) return -1;
    if (orderB != null) return 1;
    const codeA = a.product?.code || '';
    const codeB = b.product?.code || '';
    const priorityA = getProductPriority(codeA);
    const priorityB = getProductPriority(codeB);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return codeA.localeCompare(codeB);
  });
};

// Format currency - show decimals only if needed
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0';
  const num = Number(value);
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
};

function SortablePanelCard({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: !disabled ? { ...attributes, ...listeners } : {} })}
    </div>
  );
}

export default function ClientDetail() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isReadOnly, t } = useSettings();
  
  // Helper to check read-only mode and show toast
  const checkReadOnly = () => {
    if (isReadOnly) {
      toast.error("Modo solo lectura. Inicie sesión para editar.");
      return true;
    }
    return false;
  };
  
  const [client, setClient] = useState(null);
  const [branches, setBranches] = useState([]);
  const [showroomProducts, setShowroomProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addDialogType, setAddDialogType] = useState("painel"); // "painel" or "expositor"
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [addingProducts, setAddingProducts] = useState(false);
  const [uploadingShowroom, setUploadingShowroom] = useState(false);
  const [uploadingAmbiente, setUploadingAmbiente] = useState(false);
  const [notes, setNotes] = useState(""); // Notas comerciais (Comercial tab)
  const [notasExposicao, setNotasExposicao] = useState(""); // Notas de exposição (Showroom tab)
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingNotasExposicao, setSavingNotasExposicao] = useState(false);
  const [showroomChanges, setShowroomChanges] = useState([]);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [changeForm, setChangeForm] = useState({
    change_date: new Date().toISOString().split('T')[0],
    products_added: [],
    products_removed: [],
    notes: ""
  });
  const [editingChangeId, setEditingChangeId] = useState(null);
  const [addedSearch, setAddedSearch] = useState("");
  const [removedSearch, setRemovedSearch] = useState("");
  const [savingPanelSizes, setSavingPanelSizes] = useState(false);
  const [bulkApplyUsed, setBulkApplyUsed] = useState(false);
  // Catalog deliveries state
  const [clientCatalogDeliveries, setClientCatalogDeliveries] = useState([]);
  const [availableCatalogs, setAvailableCatalogs] = useState([]);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  const [catalogDeliveryForm, setCatalogDeliveryForm] = useState({
    catalog_id: "",
    quantity: 1,
    delivery_date: new Date().toISOString().split('T')[0]
  });
  const [savingCatalogDelivery, setSavingCatalogDelivery] = useState(false);
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);
  // Vendas & Rappel (fuzzy-matched by client name against imported GPP sales data)
  const [salesInfo, setSalesInfo] = useState(null);
  const [selectedSalesYear, setSelectedSalesYear] = useState('2026');
  const [annualPerformance, setAnnualPerformance] = useState(null);
  const [exposureEfficiency, setExposureEfficiency] = useState(null);
  const [rappelInfo, setRappelInfo] = useState(null);
  const [encomendaInfo, setEncomendaInfo] = useState(null);
  // Read initial tab from URL query param - if none given, land on the neutral
  // "choose Comercial or Showroom" screen (no group pre-selected)
  const requestedTab = searchParams.get('tab');
  const initialTab = requestedTab || "produtos";
  const [activeTab, setActiveTab] = useState(initialTab);
  const SHOWROOM_TAB_VALUES = ['produtos', 'expositores', 'showroom', 'ambientes', 'projetos', 'historico', 'notas_exposicao'];
  const [mainGroup, setMainGroup] = useState(() => {
    if (!requestedTab) return null;
    return SHOWROOM_TAB_VALUES.includes(requestedTab) ? 'showroom' : 'comercial';
  });
  const showroomInputRef = useRef(null);
  const ambienteInputRef = useRef(null);
  // Product notes state
  const [productNotes, setProductNotes] = useState({});
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNoteProductId, setEditingNoteProductId] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Panel filter state for summary card clicks
  const [panelFilter, setPanelFilter] = useState(null); // null = all, 'size:1920x1005', 'defect', 'custom', 'no-size'
  // Custom panels (out of catalog) state
  const [customPanels, setCustomPanels] = useState([]);
  const [showCustomPanelDialog, setShowCustomPanelDialog] = useState(false);
  const [customPanelForm, setCustomPanelForm] = useState({ name: "", image_url: "", panel_size: "" });
  const [uploadingCustomPanel, setUploadingCustomPanel] = useState(false);
  const customPanelInputRef = useRef(null);
  // Multiple custom panels upload state
  const [pendingCustomPanels, setPendingCustomPanels] = useState([]);
  const [editingPanelIndex, setEditingPanelIndex] = useState(null);
  // Edit custom panel state
  const [editingCustomPanel, setEditingCustomPanel] = useState(null);
  const [editCustomPanelForm, setEditCustomPanelForm] = useState({ name: "", panel_size: "" });
  // Lightbox state for viewing images in full size with zoom and pan
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null, name: null });
  // Image delete confirmation state
  const [imageDeleteConfirm, setImageDeleteConfirm] = useState({ 
    open: false, 
    imageUrl: null, 
    imageType: null, // 'showroom' or 'ambiente'
    previewUrl: null 
  });
  // Projects state
  const [projects, setProjects] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: "" });
  const [savingProject, setSavingProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectFileDialog, setShowProjectFileDialog] = useState(false);
  const [uploadingProjectFile, setUploadingProjectFile] = useState(false);
  const projectFileInputRef = useRef(null);
  
  // Work Order (Ordem de Trabalho) state
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [workOrderPanelsOut, setWorkOrderPanelsOut] = useState([]); // IDs of panels leaving
  const [workOrderPanelsIn, setWorkOrderPanelsIn] = useState([]); // IDs of panels entering
  const [workOrderObservations, setWorkOrderObservations] = useState("");
  const [generatingWorkOrder, setGeneratingWorkOrder] = useState(false);
  const [workOrderSearchIn, setWorkOrderSearchIn] = useState("");
  const woLoadedRef = useRef(false); // true once the saved draft (if any) has been restored from the client

  // Auto-save the Ordem de Trabalho draft (debounced) so it survives closing the dialog or
  // reloading the page - only cleared explicitly via the "Reiniciar" button.
  useEffect(() => {
    if (!woLoadedRef.current || !clientId) return;
    const timer = setTimeout(() => {
      axios.put(`${API}/clients/${clientId}/work-order-draft`, {
        panels_out: workOrderPanelsOut,
        panels_in: workOrderPanelsIn,
        observations: workOrderObservations
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
  }, [workOrderPanelsOut, workOrderPanelsIn, workOrderObservations, clientId]);

  const resetWorkOrder = async () => {
    setWorkOrderPanelsOut([]);
    setWorkOrderPanelsIn([]);
    setWorkOrderObservations("");
    setWorkOrderSearchIn("");
    try {
      await axios.delete(`${API}/clients/${clientId}/work-order-draft`);
      toast.success("Ordem de Trabalho reiniciada");
    } catch {
      toast.error("Erro ao reiniciar Ordem de Trabalho");
    }
  };
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailType, setEmailType] = useState("report"); // "report" or "work_order"

  // Client visits state
  const [clientVisits, setClientVisits] = useState([]);
  // Reclamações associadas a este cliente (tab "Reclamações" na secção Comercial)
  const [clientComplaints, setClientComplaints] = useState([]);
  // Dias desde a última visita (calendário, mesma lógica simples usada nos alertas de "sem
  // visita" no backend) - mostrado ao lado do contador de visitas no separador "Visitas".
  const daysSinceLastVisit = useMemo(() => {
    if (!clientVisits.length) return null;
    const latestDate = clientVisits.reduce((latest, v) => {
      if (!v.visit_date) return latest;
      return (!latest || v.visit_date > latest) ? v.visit_date : latest;
    }, null);
    if (!latestDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const visitDay = new Date(latestDate);
    visitDay.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today - visitDay) / (1000 * 60 * 60 * 24)));
  }, [clientVisits]);
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [visitForm, setVisitForm] = useState({
    visit_date: new Date().toISOString().split('T')[0],
    comments: ""
  });
  const [savingVisit, setSavingVisit] = useState(false);
  const [editingVisitId, setEditingVisitId] = useState(null);

  // Client tasks (Agendar) state
  const [clientTasks, setClientTasks] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteTaskId, setDeleteTaskId] = useState(null);

  const fetchClientTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { params: { entity_id: clientId, entity_type: "client" } });
      setClientTasks(res.data || []);
    } catch {
      setClientTasks([]);
    }
  }, [clientId]);

  // Send email function
  const sendEmail = async () => {
    if (!emailRecipient) {
      toast.error("Introduza um email válido");
      return;
    }
    
    setSendingEmail(true);
    try {
      if (emailType === "work_order") {
        // Send work order email with panel data
        const params = new URLSearchParams({
          recipient_email: emailRecipient,
          message: emailMessage,
          observations: workOrderObservations,
          contact_person: client?.contacts?.[0]?.name || client?.contact_person || ""
        });
        
        // Add panels arrays
        workOrderPanelsOut.forEach(p => params.append('panels_out', p));
        workOrderPanelsIn.forEach(p => params.append('panels_in', p));
        
        await axios.post(`${API}/email/send-work-order/${clientId}?${params.toString()}`);
      } else {
        // Send client report email
        const params = new URLSearchParams({
          recipient_email: emailRecipient,
          message: emailMessage
        });
        
        await axios.post(`${API}/email/send-client-report/${clientId}?${params.toString()}`);
      }
      
      toast.success(`Email enviado para ${emailRecipient}`);
      setShowEmailDialog(false);
      setEmailRecipient("");
      setEmailMessage("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Erro ao enviar email: " + getApiErrorMessage(error));
    } finally {
      setSendingEmail(false);
    }
  };

  // Filter and sort products by type
  const painelProducts = sortShowroomProducts(
    showroomProducts.filter(p => !p.showroom_type || p.showroom_type === "painel")
  );
  const expositorProducts = sortShowroomProducts(
    showroomProducts.filter(p => p.showroom_type === "expositor")
  );

  // Calculate totals by type (value comes from nested product object)
  // This is the current showroom value (for display purposes)
  const totalPaineis = painelProducts.reduce((sum, p) => sum + ((p.product?.value || p.value || 0) * (p.quantity || 1)), 0);
  const totalExpositores = expositorProducts.reduce((sum, p) => sum + ((p.product?.value || p.value || 0) * (p.quantity || 1)), 0);
  
  // Calculate total for discontinued panels (custom panels)
  const totalDescontinuados = customPanels.reduce((sum, cp) => sum + (parseFloat(cp.value) || 0), 0);
  
  // Total invested comes from backend (includes showroom + removed products via alterações)
  // ALWAYS add totalDescontinuados since backend doesn't include custom_panels in total_value
  const totalInvestido = (client?.total_value ?? (totalPaineis + totalExpositores)) + totalDescontinuados;

  const fetchClient = useCallback(async () => {
    try {
      const [clientRes, productsRes, changesRes, deliveriesRes, catalogsRes, customPanelsRes, projectsRes, visitsRes, salesRes, exposureRes, rappelRes, encomendaRes, branchesRes, annualPerfRes, complaintsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/showroom-products/client/${clientId}`),
        axios.get(`${API}/clients/${clientId}/showroom-changes`),
        axios.get(`${API}/catalog-deliveries?client_id=${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/physical-catalogs`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/custom-panels`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/visits`).catch(() => ({ data: [] })),
        axios.get(`${API}/sales/for-client/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/clients/${clientId}/eficiencia-exposicao`).catch(() => ({ data: null })),
        axios.get(`${API}/rappel/for-client/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/encomendas/for-client/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/clients/${clientId}/branches`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/annual-performance`).catch(() => ({ data: null })),
        axios.get(`${API}/complaints`, { params: { client_id: clientId } }).catch(() => ({ data: [] }))
      ]);
      setClient(clientRes.data);
      setBranches(branchesRes.data || []);
      // If this IS the matriz record but a different sucursal was set as "principal",
      // land the user there instead (branch buttons still let them switch back/forth).
      if (!clientRes.data.parent_client_id && clientRes.data.primary_branch_id && clientRes.data.primary_branch_id !== clientId) {
        navigate(`/clients/${clientRes.data.primary_branch_id}`, { replace: true });
        return;
      }
      setNotes(clientRes.data.notas_comerciais || clientRes.data.notes || "");
      setNotasExposicao(clientRes.data.notas_exposicao || "");
      setBulkApplyUsed(clientRes.data.bulk_apply_used || false);
      // Restore any in-progress Ordem de Trabalho selection saved previously
      if (clientRes.data.wo_draft) {
        setWorkOrderPanelsOut(clientRes.data.wo_draft.panels_out || []);
        setWorkOrderPanelsIn(clientRes.data.wo_draft.panels_in || []);
        setWorkOrderObservations(clientRes.data.wo_draft.observations || "");
      }
      woLoadedRef.current = true;
      setShowroomProducts(productsRes.data);
      setShowroomChanges(changesRes.data);
      setCustomPanels(customPanelsRes.data || []);
      setProjects(projectsRes.data || []);
      setClientVisits(visitsRes.data || []);
      setClientComplaints(complaintsRes.data || []);
      setSalesInfo(salesRes.data || null);
      setAnnualPerformance(annualPerfRes.data || null);
      setExposureEfficiency(exposureRes.data || null);
      setRappelInfo(rappelRes.data || null);
      setEncomendaInfo(encomendaRes.data || null);
      // Build product notes map from showroom products
      const notesMap = {};
      productsRes.data.forEach(p => {
        if (p.product_note) {
          notesMap[p.id] = p.product_note;
        }
      });
      setProductNotes(notesMap);
      // Set catalog deliveries directly (already filtered by client_id)
      setClientCatalogDeliveries(deliveriesRes.data || []);
      setAvailableCatalogs(catalogsRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar cliente");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleSelectSalesYear = useCallback((year) => {
    setSelectedSalesYear(year);
  }, []);

  useEffect(() => {
    fetchClient();
    fetchClientTasks();
  }, [fetchClient, fetchClientTasks]);

  useEffect(() => {
    if (showChangeDialog) {
      fetchCatalog();
    }
  }, [showChangeDialog]);

  const fetchCatalog = async (search = "") => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/products`, { params });
      setCatalogProducts(response.data);
    } catch (error) {
      console.error("Error fetching catalog:", error);
    }
  };

  const openAddDialog = (type = "painel") => {
    setAddDialogType(type);
    setSelectedProducts([]);
    setCatalogSearch("");
    fetchCatalog();
    setShowAddDialog(true);
  };

  const handleCatalogSearch = (e) => {
    const value = e.target.value;
    setCatalogSearch(value);
    fetchCatalog(value);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const addSelectedProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    setAddingProducts(true);
    try {
      await Promise.all(
        selectedProducts.map(productId => 
          axios.post(`${API}/showroom-products`, {
            client_id: clientId,
            product_id: productId,
            quantity: 1,
            showroom_type: addDialogType
          })
        )
      );
      toast.success(`${selectedProducts.length} produto(s) adicionado(s)`);
      setShowAddDialog(false);
      fetchClient();
    } catch (error) {
      toast.error("Erro ao adicionar produtos");
    } finally {
      setAddingProducts(false);
    }
  };

  const removeFromShowroom = async (itemId) => {
    try {
      await axios.delete(`${API}/showroom-products/${itemId}`);
      toast.success("Produto removido do showroom");
      setDeleteConfirm({ open: false, type: null, id: null, name: null });
      fetchClient();
    } catch (error) {
      toast.error("Erro ao remover produto");
    }
  };

  const confirmDelete = (type, id, name = "") => {
    setDeleteConfirm({ open: true, type, id, name });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    if (type === 'product') {
      await removeFromShowroom(id);
    } else if (type === 'customPanel') {
      await deleteCustomPanelConfirmed(id);
    }
  };

  // Union of the client's configured size options + the item's own current size,
  // so an already-assigned measurement is never hidden even if it fell out of client.panel_sizes.
  const getSizeOptions = (currentSize) => {
    const base = client?.panel_sizes || [];
    if (currentSize && currentSize !== 'None' && !base.includes(currentSize)) {
      return [...base, currentSize];
    }
    return base;
  };

  const updateProductPanelSize = async (itemId, panelSize) => {
    try {
      await axios.put(`${API}/showroom-products/${itemId}`, null, {
        params: { panel_size: panelSize || "" }
      });
      // Update local state immediately for better UX
      setShowroomProducts(prev => 
        prev.map(p => p.id === itemId ? { ...p, panel_size: panelSize || null } : p)
      );
      toast.success("Tamanho atualizado");
    } catch (error) {
      toast.error("Erro ao atualizar tamanho");
    }
  };

  // Drag & drop reordering of showroom panels, to match the physical layout of the store
  const panelDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const handlePanelDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = painelProducts.findIndex(p => p.id === active.id);
    const newIndex = painelProducts.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(painelProducts, oldIndex, newIndex);
    const orderMap = new Map(reordered.map((p, idx) => [p.id, idx]));
    setShowroomProducts(prev => prev.map(p => orderMap.has(p.id) ? { ...p, display_order: orderMap.get(p.id) } : p));
    try {
      await axios.put(`${API}/showroom-products/client/${clientId}/reorder`, { ordered_ids: reordered.map(p => p.id) });
    } catch (error) {
      toast.error("Erro ao guardar a nova ordem dos painéis");
    }
  };

  const handleImageUpload = async (files, type) => {
    if (!files || files.length === 0) return;
    
    const setUploading = type === 'showroom' ? setUploadingShowroom : setUploadingAmbiente;
    setUploading(true);
    
    try {
      for (const file of files) {
        // Upload directly to GridFS via backend
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API}/clients/${clientId}/images/${type}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(`${files.length} imagem(ns) adicionada(s)`);
      fetchClient();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erro ao carregar imagem");
    } finally {
      setUploading(false);
    }
  };

  // Open confirmation dialog for image deletion
  const confirmRemoveImage = (imageUrl, type) => {
    setImageDeleteConfirm({
      open: true,
      imageUrl: imageUrl,
      imageType: type,
      previewUrl: resolveImageUrl(imageUrl)
    });
  };

  const removeImage = async (imageUrl, type) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/images/${type}?image_url=${encodeURIComponent(imageUrl)}`);
      toast.success("Imagem removida");
      fetchClient();
    } catch (error) {
      toast.error("Erro ao remover imagem");
    }
  };

  // Handle confirmed image deletion
  const handleConfirmImageDelete = async () => {
    if (imageDeleteConfirm.imageUrl && imageDeleteConfirm.imageType) {
      await removeImage(imageDeleteConfirm.imageUrl, imageDeleteConfirm.imageType);
    }
    setImageDeleteConfirm({ open: false, imageUrl: null, imageType: null, previewUrl: null });
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        ...client,
        notas_comerciais: notes
      });
      setClient(prev => ({ ...prev, notas_comerciais: notes }));
      toast.success("Notas guardadas");
    } catch (error) {
      toast.error("Erro ao guardar notas");
    } finally {
      setSavingNotes(false);
    }
  };

  const saveNotasExposicao = async () => {
    setSavingNotasExposicao(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        ...client,
        notas_exposicao: notasExposicao
      });
      setClient(prev => ({ ...prev, notas_exposicao: notasExposicao }));
      toast.success("Notas guardadas");
    } catch (error) {
      toast.error("Erro ao guardar notas");
    } finally {
      setSavingNotasExposicao(false);
    }
  };

  const handleAddChange = async () => {
    if (changeForm.products_added.length === 0 && changeForm.products_removed.length === 0) {
      toast.error("Adicione pelo menos um produto");
      return;
    }
    try {
      if (editingChangeId) {
        await axios.put(`${API}/clients/${clientId}/showroom-changes/${editingChangeId}`, changeForm);
        toast.success("Alteração atualizada");
      } else {
        await axios.post(`${API}/clients/${clientId}/showroom-changes`, changeForm);
        toast.success("Alteração registada");
      }
      setShowChangeDialog(false);
      setEditingChangeId(null);
      setAddedSearch("");
      setRemovedSearch("");
      setChangeForm({
        change_date: new Date().toISOString().split('T')[0],
        products_added: [],
        products_removed: [],
        notes: ""
      });
      fetchClient();
    } catch (error) {
      toast.error(editingChangeId ? "Erro ao atualizar alteração" : "Erro ao registar alteração");
    }
  };

  const handleEditChange = (change) => {
    setEditingChangeId(change.id);
    setChangeForm({
      change_date: change.change_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      products_added: change.products_added?.map(p => p.id) || [],
      products_removed: change.products_removed?.map(p => p.id) || [],
      notes: change.notes || ""
    });
    setShowChangeDialog(true);
  };

  const handleDeleteChange = async (changeId) => {
    const confirmed = window.confirm("Tem a certeza que deseja eliminar este registo?");
    console.log("Delete confirmed:", confirmed, "Change ID:", changeId);
    if (!confirmed) return;
    
    try {
      console.log("Deleting change:", changeId);
      const response = await axios.delete(`${API}/clients/${clientId}/showroom-changes/${changeId}`);
      console.log("Delete response:", response.data);
      toast.success("Registo eliminado");
      fetchClient();
    } catch (error) {
      console.error("Error deleting change:", error.response?.data || error.message);
      toast.error(getApiErrorMessage(error, "Erro ao eliminar registo"));
    }
  };

  const toggleProductInChange = (productId, type) => {
    setChangeForm(prev => {
      const list = type === 'added' ? 'products_added' : 'products_removed';
      const currentList = prev[list];
      if (currentList.includes(productId)) {
        return { ...prev, [list]: currentList.filter(id => id !== productId) };
      } else {
        return { ...prev, [list]: [...currentList, productId] };
      }
    });
  };

  const handlePanelSizeToggle = async (size) => {
    const currentSizes = client.panel_sizes || [];
    let newSizes;
    
    if (currentSizes.includes(size)) {
      newSizes = currentSizes.filter(s => s !== size);
    } else {
      newSizes = [...currentSizes, size];
    }
    
    setSavingPanelSizes(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, {
        ...client,
        panel_sizes: newSizes
      });
      setClient(prev => ({ ...prev, panel_sizes: newSizes }));
      toast.success("Tamanho atualizado");
    } catch (error) {
      toast.error("Erro ao atualizar tamanho");
    } finally {
      setSavingPanelSizes(false);
    }
  };

  const applyPanelSizeToAll = async (size) => {
    setSavingPanelSizes(true);
    try {
      const response = await axios.put(`${API}/showroom-products/client/${clientId}/bulk-panel-size`, null, {
        params: { panel_size: size }
      });
      // Update local state
      setShowroomProducts(prev => prev.map(p => ({ ...p, panel_size: size })));
      setBulkApplyUsed(true);
      toast.success(`Tamanho "${size}" aplicado a todos os produtos`);
    } catch (error) {
      console.error("Bulk update error:", error);
      toast.error("Erro ao aplicar tamanho");
    } finally {
      setSavingPanelSizes(false);
    }
  };

  // Catalog delivery functions
  const handleAddCatalogDelivery = async () => {
    if (!catalogDeliveryForm.catalog_id) {
      toast.error("Selecione um catálogo");
      return;
    }
    
    setSavingCatalogDelivery(true);
    try {
      if (editingDeliveryId) {
        // Update existing delivery
        await axios.put(`${API}/catalog-deliveries/${editingDeliveryId}`, {
          quantity: catalogDeliveryForm.quantity,
          delivery_date: catalogDeliveryForm.delivery_date
        });
        toast.success("Entrega atualizada com sucesso");
      } else {
        // Create new delivery
        await axios.post(`${API}/catalog-deliveries`, {
          catalog_id: catalogDeliveryForm.catalog_id,
          client_id: client.id,
          quantity: catalogDeliveryForm.quantity,
          delivery_date: catalogDeliveryForm.delivery_date
        });
        toast.success("Entrega registada com sucesso");
      }
      setShowCatalogDialog(false);
      setEditingDeliveryId(null);
      setCatalogDeliveryForm({
        catalog_id: "",
        quantity: 1,
        delivery_date: new Date().toISOString().split('T')[0]
      });
      fetchClient();
    } catch (error) {
      toast.error(editingDeliveryId ? "Erro ao atualizar entrega" : "Erro ao registar entrega");
    } finally {
      setSavingCatalogDelivery(false);
    }
  };

  const openEditDelivery = (delivery) => {
    setEditingDeliveryId(delivery.id);
    setCatalogDeliveryForm({
      catalog_id: delivery.catalog_id,
      quantity: delivery.quantity,
      delivery_date: delivery.delivery_date?.split('T')[0] || new Date().toISOString().split('T')[0]
    });
    setShowCatalogDialog(true);
  };

  const handleDeleteCatalogDelivery = async (deliveryId) => {
    if (!window.confirm("Tem certeza que deseja eliminar esta entrega?")) return;
    
    try {
      await axios.delete(`${API}/catalog-deliveries/${deliveryId}`);
      toast.success("Entrega eliminada");
      fetchClient();
    } catch (error) {
      toast.error("Erro ao eliminar entrega");
    }
  };

  const getCatalogName = (catalogId) => {
    const catalog = availableCatalogs.find(c => c.id === catalogId);
    return catalog?.name || "Catálogo desconhecido";
  };

  // Product Notes functions
  const openNoteDialog = (productId) => {
    setEditingNoteProductId(productId);
    setNoteText(productNotes[productId] || "");
    setShowNoteDialog(true);
  };

  const saveProductNote = async () => {
    if (!editingNoteProductId) return;
    
    setSavingNote(true);
    try {
      await axios.put(`${API}/showroom-products/${editingNoteProductId}`, null, {
        params: { product_note: noteText || "" }
      });
      setProductNotes(prev => ({
        ...prev,
        [editingNoteProductId]: noteText || null
      }));
      toast.success(noteText ? "Nota guardada" : "Nota removida");
      setShowNoteDialog(false);
    } catch (error) {
      toast.error("Erro ao guardar nota");
    } finally {
      setSavingNote(false);
    }
  };

  // Custom Panels (out of catalog) functions
  const handleCustomPanelUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingCustomPanel(true);
    const newPanels = [];
    
    try {
      for (const file of Array.from(files)) {
        // Upload to GridFS via backend
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/clients/${clientId}/custom-panels/upload-image`, 
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        if (response.data.image_url) {
          newPanels.push({
            name: response.data.default_name || file.name.replace(/\.[^/.]+$/, ""),
            image_url: response.data.image_url,
            panel_size: client.panel_sizes?.[0] || "",
            tempId: Date.now() + Math.random()
          });
        }
      }
      
      if (newPanels.length > 0) {
        setPendingCustomPanels(prev => [...prev, ...newPanels]);
        toast.success(`${newPanels.length} imagem(ns) carregada(s)`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erro ao carregar imagens");
    } finally {
      setUploadingCustomPanel(false);
      if (customPanelInputRef.current) {
        customPanelInputRef.current.value = '';
      }
    }
  };

  const updatePendingPanelName = (tempId, newName) => {
    setPendingCustomPanels(prev => 
      prev.map(p => p.tempId === tempId ? { ...p, name: newName } : p)
    );
  };

  const updatePendingPanelSize = (tempId, newSize) => {
    setPendingCustomPanels(prev => 
      prev.map(p => p.tempId === tempId ? { ...p, panel_size: newSize } : p)
    );
  };

  const removePendingPanel = (tempId) => {
    setPendingCustomPanels(prev => prev.filter(p => p.tempId !== tempId));
  };

  const saveAllPendingPanels = async () => {
    if (pendingCustomPanels.length === 0) {
      toast.error("Adicione pelo menos uma imagem");
      return;
    }

    // Check all panels have names
    const invalidPanels = pendingCustomPanels.filter(p => !p.name.trim());
    if (invalidPanels.length > 0) {
      toast.error("Todos os painéis precisam de um nome");
      return;
    }

    setUploadingCustomPanel(true);
    let successCount = 0;
    
    try {
      for (const panel of pendingCustomPanels) {
        await axios.post(`${API}/clients/${clientId}/custom-panels`, {
          name: panel.name,
          image_url: panel.image_url,
          panel_size: panel.panel_size || null
        });
        successCount++;
      }
      
      toast.success(`${successCount} painel(is) adicionado(s)`);
      setShowCustomPanelDialog(false);
      setPendingCustomPanels([]);
      setCustomPanelForm({ name: "", image_url: "" });
      fetchClient();
    } catch (error) {
      toast.error("Erro ao guardar painéis");
    } finally {
      setUploadingCustomPanel(false);
    }
  };

  const addCustomPanel = async () => {
    if (!customPanelForm.name || !customPanelForm.image_url) {
      toast.error("Preencha o nome e carregue uma imagem");
      return;
    }

    try {
      await axios.post(`${API}/clients/${clientId}/custom-panels`, customPanelForm);
      toast.success("Painel adicionado");
      setShowCustomPanelDialog(false);
      setCustomPanelForm({ name: "", image_url: "" });
      fetchClient();
    } catch (error) {
      toast.error("Erro ao adicionar painel");
    }
  };

  const deleteCustomPanelConfirmed = async (panelId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/custom-panels/${panelId}`);
      toast.success("Painel eliminado");
      setDeleteConfirm({ open: false, type: null, id: null, name: null });
      fetchClient();
    } catch (error) {
      toast.error("Erro ao eliminar painel");
    }
  };

  const deleteCustomPanel = (panelId, panelName) => {
    confirmDelete('customPanel', panelId, panelName);
  };

  // Edit custom panel functions
  const openEditCustomPanel = (panel) => {
    setEditingCustomPanel(panel);
    setEditCustomPanelForm({ 
      name: panel.name, 
      panel_size: panel.panel_size || "", 
      value: panel.value || "" 
    });
  };

  const saveEditCustomPanel = async () => {
    if (!editCustomPanelForm.name.trim()) {
      toast.error("O nome é obrigatório");
      return;
    }
    
    try {
      await axios.put(`${API}/clients/${clientId}/custom-panels/${editingCustomPanel.id}`, {
        name: editCustomPanelForm.name,
        panel_size: editCustomPanelForm.panel_size || null,
        value: editCustomPanelForm.value ? parseFloat(editCustomPanelForm.value) : 0
      });
      toast.success("Painel atualizado");
      setEditingCustomPanel(null);
      fetchClient();
    } catch (error) {
      toast.error("Erro ao atualizar painel");
    }
  };

  // Project functions
  const createProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error("Introduza um nome para o projeto");
      return;
    }
    setSavingProject(true);
    try {
      await axios.post(`${API}/clients/${clientId}/projects`, { name: projectForm.name.trim() });
      toast.success("Projeto criado");
      setShowProjectDialog(false);
      setProjectForm({ name: "" });
      fetchClient();
    } catch (error) {
      toast.error("Erro ao criar projeto");
    } finally {
      setSavingProject(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este projeto e todos os seus ficheiros?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/projects/${projectId}`);
      toast.success("Projeto eliminado");
      setSelectedProject(null);
      fetchClient();
    } catch (error) {
      toast.error("Erro ao eliminar projeto");
    }
  };

  // Visit CRUD functions
  const saveVisit = async () => {
    if (!visitForm.visit_date) {
      toast.error("Selecione uma data");
      return;
    }
    
    setSavingVisit(true);
    try {
      if (editingVisitId) {
        await axios.put(`${API}/clients/${clientId}/visits/${editingVisitId}`, visitForm);
        toast.success("Visita atualizada");
      } else {
        await axios.post(`${API}/clients/${clientId}/visits`, visitForm);
        toast.success("Visita registada");
      }
      setShowVisitDialog(false);
      setVisitForm({ visit_date: new Date().toISOString().split('T')[0], comments: "" });
      setEditingVisitId(null);
      fetchClient();
    } catch (error) {
      toast.error("Erro ao guardar visita");
    } finally {
      setSavingVisit(false);
    }
  };

  const deleteVisit = async (visitId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta visita?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/visits/${visitId}`);
      toast.success("Visita eliminada");
      fetchClient();
    } catch (error) {
      toast.error("Erro ao eliminar visita");
    }
  };

  const openEditVisit = (visit) => {
    setEditingVisitId(visit.id);
    setVisitForm({
      visit_date: visit.visit_date,
      comments: visit.comments || ""
    });
    setShowVisitDialog(true);
  };

  const handleProjectFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedProject) return;

    setUploadingProjectFile(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of Array.from(files)) {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        
        if (!isImage && !isPdf) {
          errorCount++;
          continue;
        }

        try {
          const formData = new FormData();
          formData.append('file', file);
          
          if (isImage) {
            // Upload image to GridFS via backend
            await axios.post(
              `${API}/clients/${clientId}/projects/${selectedProject.id}/files/upload-image`, 
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
            );
            successCount++;
          } else {
            // Upload PDF to GridFS via backend
            await axios.post(
              `${API}/clients/${clientId}/projects/${selectedProject.id}/files/upload`, 
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }
            );
            successCount++;
          }
        } catch (err) {
          console.error('Error uploading file:', file.name, err);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} ficheiro(s) adicionado(s) ao projeto`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} ficheiro(s) não foram carregados`);
      }
      
      setShowProjectFileDialog(false);
      fetchClient();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erro ao carregar ficheiros");
    } finally {
      setUploadingProjectFile(false);
      if (projectFileInputRef.current) {
        projectFileInputRef.current.value = '';
      }
    }
  };

  const deleteProjectFile = async (projectId, fileId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar este ficheiro?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/projects/${projectId}/files/${fileId}`);
      toast.success("Ficheiro eliminado");
      fetchClient();
    } catch (error) {
      toast.error("Erro ao eliminar ficheiro");
    }
  };

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-stone-500">Cliente não encontrado</p>
        <Link to="/clients" className="btn-primary mt-4 inline-flex">
          <ArrowLeft size={18} />
          Voltar a Clientes
        </Link>
      </div>
    );
  }

  // Get products that are already in the showroom (only for paineis, expositores can be added multiple times)
  const showroomPainelProductIds = showroomProducts
    .filter(sp => sp.showroom_type !== "expositor")
    .map(sp => sp.product_id);

  return (
    <div className="flex-1 flex flex-col justify-start pt-2 animate-fade-in relative" data-testid="client-detail-page">
      {/* Everything below (logo/name/back button AND the icon/cities/big-buttons block) is
          ONE single group that gets vertically centered as a whole within the white area
          between the top nav and the footer, so the space above equals the space below. */}
      <div className="relative z-10">
      {/* Header: logo pinned far-left, name+city pinned far-right.
          The Maps pin now lives inside the Tabs header-stack below, directly above the
          city buttons, so it visually acts as the "title" of that button group. */}
      <div className="relative">
        <button 
          onClick={() => navigate(-1)} 
          className="absolute top-0 left-0 p-2 hover:bg-stone-100 rounded-lg transition-colors z-10" 
          data-testid="btn-back"
        >
          <ArrowLeft size={20} className="text-stone-600" />
        </button>
        <div className="flex flex-col items-center text-center gap-3" data-testid="client-header-row">
          {(client.logo_url || branches[0]?.matriz_logo_url) ? (
            <img 
              src={client.logo_url || branches[0]?.matriz_logo_url} 
              alt={`Logo ${client.name}`}
              className="w-20 h-20 sm:w-36 sm:h-36 object-contain border border-stone-200 rounded-2xl bg-white shadow-sm flex-shrink-0"
            />
          ) : (
            <div className="w-20 h-20 sm:w-36 sm:h-36 bg-stone-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl sm:text-4xl font-bold text-stone-400">{client.name.charAt(0)}</span>
            </div>
          )}

          <div className="min-w-0">
            <h1 className="text-2xl sm:text-4xl font-bold font-[Manrope] text-stone-900">{client.name}</h1>
            {client.city && (
              <p className="flex items-center justify-center gap-1 text-stone-500 text-sm sm:text-base mt-1" data-testid="client-city-label">
                <span className="truncate">{client.city}</span>
              </p>
            )}
          </div>

          {/* Map pin + branch city buttons */}
          <div className="flex flex-col items-center gap-1.5">
            {(client.gps_coordinates || client.address || client.city) && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.gps_coordinates || [client.address, client.city, client.country].filter(Boolean).join(', '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-[#009392]/10 text-[#009392] hover:bg-[#009392]/20 transition-colors"
                title="Ver no Google Maps"
                data-testid="link-client-maps"
              >
                <MapPin size={20} weight="fill" />
              </a>
            )}
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="gresart-tabs">
        {/* Entry screen: only the 2 big centered buttons until one is chosen. */}
        {(() => {
          const GROUPS = {
            comercial: {
              label: t('client_tab_comercial') || 'Comercial',
              items: [
                { value: 'vendas', label: 'Vendas' },
                { value: 'encomendas', label: `Encomendas${encomendaInfo ? ` (${encomendaInfo.item_count})` : ''}` },
                { value: 'rappel', label: 'Rappel' },
                { value: 'visitas', label: `Visitas (${clientVisits.length})` },
                { value: 'agendar', label: `Agendar (${clientTasks.filter(tsk => tsk.status === 'pendente').length})` },
                { value: 'catalogos', label: `Catálogos (${clientCatalogDeliveries.length})` },
                { value: 'reclamacoes', label: `Reclamações (${clientComplaints.length})` },
                { value: 'notas', label: 'Notas' },
              ],
            },
            showroom: {
              label: t('client_tab_showroom') || 'Showroom',
              items: [
                { value: 'produtos', label: `Painéis Técnicos (${painelProducts.length + customPanels.length})` },
                { value: 'expositores', label: `Expositores (${expositorProducts.length})` },
                { value: 'showroom', label: `Showroom (${client.showroom_images?.length || 0})` },
                { value: 'ambientes', label: `Ambientes (${client.ambiente_images?.length || 0})` },
                { value: 'projetos', label: `Projetos (${projects.length})` },
                { value: 'historico', label: `Alterações (${showroomChanges.length})` },
                { value: 'catalogos', label: `Catálogos (${clientCatalogDeliveries.length})` },
                { value: 'eficiencia_exposicao', label: 'Eficiência' },
                { value: 'notas_exposicao', label: 'Notas' },
              ],
            },
          };

          const COLUMN_KEYS = ['comercial', 'showroom'];

          return (
            <div className="mb-1 w-full flex flex-col items-center" data-testid="client-header-stack">
              {/* Big buttons block: fixed 250x80px, centered.
                  Left: COMERCIAL -> Vendas. Right: SHOWROOM -> Investido. Wrapped in an
                  overflow-x-auto strip so the fixed 250px width never breaks page layout
                  on narrow mobile viewports (desktop math/centering is unaffected). */}
              <div className="w-full overflow-x-auto mt-6">
                <div className="grid grid-cols-2 gap-x-6 w-[524px] mx-auto" data-testid="client-header-columns">
                  {COLUMN_KEYS.map((key, idx) => {
                    const group = GROUPS[key];
                    const isActive = mainGroup === key;
                    const branch = branches[idx];
                    return (
                      <div
                        key={key}
                        className="w-full flex flex-col items-center gap-3"
                        data-testid={`client-header-col-${key}`}
                      >
                        {branches.length > 1 && branch && (
                          <Button
                            type="button"
                            variant={branch.id === clientId ? "default" : "outline"}
                            size="sm"
                            onClick={() => { if (branch.id !== clientId) navigate(`/clients/${branch.id}`); }}
                            className={`w-full ${branch.id === clientId ? "bg-[#009392] hover:bg-[#007a7a]" : "border-[#009392]/30 text-[#009392]"}`}
                            data-testid={`branch-city-btn-${branch.id}`}
                          >
                            <Buildings size={14} className="mr-1" />
                            {branch.city || branch.name}
                          </Button>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setMainGroup(key);
                            if (!group.items.some(i => i.value === activeTab)) {
                              setActiveTab(group.items[0]?.value);
                            }
                          }}
                          className={`w-full h-[80px] rounded-3xl uppercase text-xl font-bold tracking-wide text-center transition-all duration-200 ${
                            isActive
                              ? 'bg-[#009392] text-white shadow-lg shadow-[#009392]/30 scale-[1.03]'
                              : 'bg-white text-stone-500 border-2 border-stone-200 hover:border-[#009392]/50 hover:text-[#009392]'
                          }`}
                          data-testid={`main-group-${key}`}
                        >
                          {group.label}
                        </button>

                        <div
                          className="w-full px-3 py-2 rounded-full bg-[#009392]/10 text-[#00625f] border border-[#009392]/15 text-sm font-medium text-center leading-tight break-words"
                          data-testid={`mini-stat-${key === 'comercial' ? 'vendas' : 'investido'}`}
                        >
                          {key === 'comercial'
                            ? `Vendas: €${formatCurrency(salesInfo?.current_sales || 0)}`
                            : `Investido: €${formatCurrency(totalInvestido)}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {mainGroup && (
                <>
                  <div className="flex flex-wrap justify-center gap-2 mt-3" data-testid={`subtabs-${mainGroup}`}>
                    {GROUPS[mainGroup].items.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setActiveTab(item.value)}
                        className={`px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 ${
                          activeTab === item.value
                            ? 'bg-[#009392]/15 text-[#009392] border border-[#009392]'
                            : 'bg-stone-100 text-stone-500 border border-transparent hover:bg-stone-200'
                        }`}
                        data-testid={`tab-${item.value}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {mainGroup && (
        <>
        <TabsContent value="produtos">
          {/* Showroom Products */}
          <div className="gresart-card">
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                Painéis Técnicos no Showroom
              </h2>
              <div className="flex items-center gap-2 flex-wrap" data-testid="showroom-actions">
                <Button
                  onClick={() => {
                    setShowWorkOrderDialog(true);
                    axios.get(`${API}/products?product_type=PAINÉIS TÉCNICOS`).then(res => {
                      setCatalogProducts(res.data || []);
                    }).catch(() => {});
                  }}
                  variant="outline"
                  className="flex items-center gap-1 px-3 py-2 border-amber-500 text-amber-600 hover:bg-amber-50 text-xs sm:text-sm font-medium"
                  title="Criar Ordem de Trabalho"
                  data-testid="btn-work-order"
                >
                  <NotePencil size={16} />
                  <span>Ordem Trabalho</span>
                </Button>
                <Link 
                  to={`/reports/${clientId}`} 
                  className="btn-secondary px-3 py-2 text-xs sm:text-sm"
                  data-testid="btn-generate-report"
                  title="Gerar Relatório PDF"
                >
                  <FileText size={16} />
                  <span>PDF</span>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEmailType("report");
                    setShowEmailDialog(true);
                  }}
                  className="px-3 py-2 border-[#009392] text-[#009392] hover:bg-[#009392]/10 text-xs sm:text-sm"
                  data-testid="btn-send-email"
                  title="Enviar por Email"
                >
                  <EnvelopeSimple size={16} />
                  <span className="ml-1">Email</span>
                </Button>
              </div>
            </div>

            {/* Panel Sizes Selector + Add Button */}
            <div className="bg-stone-50 rounded-lg p-4 mb-6 border border-stone-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className="text-sm font-medium text-stone-700">Tamanho dos Painéis:</span>
                  <div className="flex flex-wrap gap-2">
                    {PANEL_SIZES.map(size => {
                      const isSelected = (client.panel_sizes || []).includes(size);
                      return (
                        <button
                          key={size}
                          onClick={() => handlePanelSizeToggle(size)}
                          disabled={savingPanelSizes}
                          className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                            isSelected
                              ? 'bg-[#009392] text-white border-[#009392]'
                              : 'bg-white text-stone-600 border-stone-300 hover:border-[#009392] hover:text-[#009392]'
                          } ${savingPanelSizes ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`panel-size-${size}`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {!isReadOnly && (
                  <Button 
                    className="btn-primary" 
                    onClick={() => openAddDialog("painel")} 
                    data-testid="btn-add-products"
                  >
                    <Plus size={18} />
                    Adicionar Produtos
                  </Button>
                )}
              </div>
              {/* Bulk Apply Button - Hidden after use */}
              {!bulkApplyUsed && (client.panel_sizes?.length || 0) > 0 && showroomProducts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-stone-200">
                  <span className="text-xs text-stone-500 self-center mr-2">Aplicar a todos:</span>
                  {client.panel_sizes.map(size => (
                    <button
                      key={`apply-${size}`}
                      onClick={() => applyPanelSizeToAll(size)}
                      disabled={savingPanelSizes}
                      className="px-3 py-1 text-xs rounded border border-[#009392] text-[#009392] hover:bg-[#009392] hover:text-white transition-all disabled:opacity-50"
                      data-testid={`apply-all-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Statistics Summary - Full Width Cards - CLICKABLE FILTERS */}
            {(painelProducts.length > 0 || customPanels.length > 0) && (
              <div className="mb-6 bg-stone-50 rounded-lg p-4 border border-stone-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-stone-700">
                    Resumo de Painéis Técnicos
                  </h4>
                  {panelFilter && (
                    <button
                      onClick={() => setPanelFilter(null)}
                      className="text-xs text-[#009392] hover:text-[#007a7a] flex items-center gap-1"
                    >
                      <X size={14} /> Limpar filtro
                    </button>
                  )}
                </div>
                {/* Mobile: 2-column grid, Desktop: flex row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-row gap-2 md:gap-3 w-full">
                  {/* Small cards - grow equally */}
                  {PANEL_SIZES.map(size => {
                    const count = painelProducts.filter(p => p.panel_size === size).length;
                    const customCount = customPanels.filter(cp => cp.panel_size === size).length;
                    const total = count + customCount;
                    const isActive = panelFilter === `size:${size}`;
                    return (
                      <button
                        key={size}
                        onClick={() => setPanelFilter(isActive ? null : `size:${size}`)}
                        className={`bg-white rounded-lg p-2 md:p-3 border shadow-sm h-[72px] md:h-[88px] md:flex-1 flex flex-col justify-between text-left transition-all ${
                          isActive 
                            ? 'border-[#009392] ring-2 ring-[#009392]/30' 
                            : 'border-stone-200 hover:border-[#009392]/50'
                        } ${total > 0 ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        disabled={total === 0}
                        data-testid={`filter-size-${size}`}
                      >
                        <div>
                          <p className="text-[10px] md:text-xs text-stone-500">Medida</p>
                          <p className="text-xs md:text-sm font-medium text-stone-700 truncate">{size}</p>
                        </div>
                        <p className={`text-xl md:text-2xl font-bold ${total > 0 ? 'text-[#009392]' : 'text-stone-300'}`}>{total}</p>
                      </button>
                    );
                  })}
                  
                  {/* Painéis SIN tamaño asignado */}
                  {(() => {
                    const noSizeCount = painelProducts.filter(p => !p.panel_size || p.panel_size === 'None').length;
                    const noSizeCustom = customPanels.filter(cp => !cp.panel_size || cp.panel_size === 'None').length;
                    const totalNoSize = noSizeCount + noSizeCustom;
                    const isActive = panelFilter === 'no-size';
                    if (totalNoSize > 0) {
                      return (
                        <button
                          onClick={() => setPanelFilter(isActive ? null : 'no-size')}
                          className={`bg-white rounded-lg p-2 md:p-3 border shadow-sm h-[72px] md:h-[88px] md:flex-1 flex flex-col justify-between text-left transition-all cursor-pointer ${
                            isActive 
                              ? 'border-red-500 ring-2 ring-red-300' 
                              : 'border-red-200 hover:border-red-400'
                          }`}
                          data-testid="filter-no-size"
                        >
                          <div>
                            <p className="text-[10px] md:text-xs text-red-500">⚠️ Sin medida</p>
                          </div>
                          <p className="text-xl md:text-2xl font-bold text-red-500">{totalNoSize}</p>
                        </button>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Painéis com defeito */}
                  {(() => {
                    const defectCount = painelProducts.filter(p => productNotes[p.id]).length;
                    const isActive = panelFilter === 'defect';
                    return (
                      <button
                        onClick={() => defectCount > 0 && setPanelFilter(isActive ? null : 'defect')}
                        className={`bg-white rounded-lg p-2 md:p-3 border shadow-sm h-[72px] md:h-[88px] md:flex-1 flex flex-col justify-between text-left transition-all ${
                          isActive 
                            ? 'border-amber-500 ring-2 ring-amber-300' 
                            : 'border-amber-200 hover:border-amber-400'
                        } ${defectCount > 0 ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        disabled={defectCount === 0}
                        data-testid="filter-defect"
                      >
                        <p className="text-[10px] md:text-xs text-stone-500">Com Defeito</p>
                        <p className="text-xl md:text-2xl font-bold text-amber-500">{defectCount}</p>
                      </button>
                    );
                  })()}
                  
                  {/* Descontinuados PPM */}
                  {(() => {
                    const discontinuedCount = painelProducts.filter(p => p.product?.is_discontinued).length;
                    const isActive = panelFilter === 'discontinued';
                    return (
                      <button
                        onClick={() => discontinuedCount > 0 && setPanelFilter(isActive ? null : 'discontinued')}
                        className={`bg-white rounded-lg p-2 md:p-3 border shadow-sm h-[72px] md:h-[88px] md:flex-1 flex flex-col justify-between text-left transition-all ${
                          isActive 
                            ? 'border-stone-800 ring-2 ring-stone-400' 
                            : 'border-stone-300 hover:border-stone-500'
                        } ${discontinuedCount > 0 ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        disabled={discontinuedCount === 0}
                        data-testid="filter-discontinued"
                      >
                        <p className="text-[10px] md:text-xs text-stone-500">Descontinuados</p>
                        <p className="text-xl md:text-2xl font-bold text-stone-800">{discontinuedCount}</p>
                      </button>
                    );
                  })()}
                  
                  {/* Fora de Catálogo */}
                  {(() => {
                    const isActive = panelFilter === 'custom';
                    return (
                      <button
                        onClick={() => customPanels.length > 0 && setPanelFilter(isActive ? null : 'custom')}
                        className={`bg-white rounded-lg p-2 md:p-3 border shadow-sm h-[72px] md:h-[88px] md:flex-1 flex flex-col justify-between text-left transition-all ${
                          isActive 
                            ? 'border-purple-500 ring-2 ring-purple-300' 
                            : 'border-purple-200 hover:border-purple-400'
                        } ${customPanels.length > 0 ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                        disabled={customPanels.length === 0}
                        data-testid="filter-custom"
                      >
                        <p className="text-[10px] md:text-xs text-stone-500">Fora Catalogo</p>
                        <p className="text-xl md:text-2xl font-bold text-purple-500">{customPanels.length}</p>
                      </button>
                    );
                  })()}
                  
                  {/* Total - Full width on mobile, double width on desktop */}
                  <button
                    onClick={() => setPanelFilter(null)}
                    className={`bg-[#009392] rounded-lg p-2 md:p-3 shadow-lg flex flex-col items-center justify-center cursor-pointer transition-all col-span-2 md:col-span-1 md:flex-[2] h-[72px] md:h-[88px] ${
                      panelFilter === null ? 'ring-2 ring-white ring-offset-2 ring-offset-[#009392]' : 'hover:bg-[#00a8a6]'
                    }`}
                    data-testid="filter-all"
                  >
                    <p className="text-[10px] md:text-xs text-white/80">Total</p>
                    <p className="text-2xl md:text-4xl font-bold text-white">{painelProducts.length + customPanels.length}</p>
                    <p className="text-[10px] md:text-xs text-white/80">Painéis</p>
                  </button>
                </div>
              </div>
            )}

            {painelProducts.length === 0 && !panelFilter ? (
              <div className="empty-state py-12">
                <Package size={48} className="text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem produtos atribuídos</h3>
                <p className="text-stone-500 mb-4">Adicione produtos do catálogo a este showroom</p>
                <Button className="btn-primary" onClick={() => openAddDialog("painel")} data-testid="btn-add-first-product">
                  <Plus size={18} />
                  Adicionar Produtos
                </Button>
              </div>
            ) : (
              <>
                {/* Filter indicator */}
                {panelFilter && (
                  <div className="mb-4 p-3 bg-[#009392]/10 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-[#009392] font-medium">
                      {panelFilter === 'defect' && `Mostrando: Painéis com Defeito`}
                      {panelFilter === 'discontinued' && `Mostrando: Painéis Descontinuados`}
                      {panelFilter === 'custom' && `Mostrando: Painéis Fora de Catálogo`}
                      {panelFilter === 'no-size' && `Mostrando: Painéis sem Medida`}
                      {panelFilter?.startsWith('size:') && `Mostrando: Medida ${panelFilter.replace('size:', '')}`}
                    </span>
                    <button
                      onClick={() => setPanelFilter(null)}
                      className="text-sm text-[#009392] hover:underline"
                    >
                      Ver todos
                    </button>
                  </div>
                )}
                
                {/* Filtered products grid */}
                <div className="product-grid">
                <DndContext sensors={panelDndSensors} collisionDetection={closestCenter} onDragEnd={handlePanelDragEnd}>
                <SortableContext items={painelProducts.map(p => p.id)} strategy={rectSortingStrategy}>
                {painelProducts
                  .filter(item => {
                    if (!panelFilter) return true;
                    if (panelFilter === 'defect') return productNotes[item.id];
                    if (panelFilter === 'discontinued') return item.product?.is_discontinued;
                    if (panelFilter === 'no-size') return !item.panel_size || item.panel_size === 'None';
                    if (panelFilter === 'custom') return false; // Custom panels shown separately
                    if (panelFilter.startsWith('size:')) {
                      const size = panelFilter.replace('size:', '');
                      return item.panel_size === size;
                    }
                    return true;
                  })
                  .map((item) => (
                  <SortablePanelCard key={item.id} id={item.id} disabled={!!panelFilter || isReadOnly}>
                    {({ dragHandleProps }) => (
                  <div 
                    className={`product-card flex flex-col ${
                      item.product?.is_discontinued 
                        ? 'ring-2 ring-stone-800 ring-offset-2 bg-stone-100' 
                        : ((!item.panel_size || item.panel_size === 'None') ? 'ring-2 ring-red-400 ring-offset-2' : '')
                    }`}
                    data-testid={`showroom-product-${item.id}`}
                  >
                    {item.product ? (
                      <>
                        <div className="relative bg-stone-50 p-2">
                          {/* Warning badge for missing panel size */}
                          {(!item.panel_size || item.panel_size === 'None') && !item.product?.is_discontinued && (
                            <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-xs py-1 px-2 text-center font-medium z-20">
                              ⚠️ SIN MEDIDA
                            </div>
                          )}
                          {!panelFilter && !isReadOnly && (
                            <button
                              {...dragHandleProps}
                              className="absolute bottom-2 right-2 z-20 p-1 bg-white/90 rounded-md text-stone-400 hover:text-stone-600 cursor-grab active:cursor-grabbing touch-none shadow-sm"
                              data-testid={`drag-handle-panel-${item.id}`}
                              title="Arrastar para reordenar"
                            >
                              <DotsSixVertical size={14} weight="bold" />
                            </button>
                          )}
                          <OptimizedImage 
                            src={item.product.image_url} 
                            alt={item.product.code}
                            className="w-full h-auto max-h-64 object-contain mx-auto cursor-pointer hover:opacity-90 transition-opacity"
                            width={300}
                            onClick={() => setLightboxImage(item.product.image_url)}
                          />
                          {/* Note indicator button */}
                          <button
                            onClick={() => openNoteDialog(item.id)}
                            className={`absolute top-2 left-2 p-1.5 rounded-full shadow-sm transition-colors ${
                              productNotes[item.id] 
                                ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
                                : 'bg-white/90 text-stone-400 hover:bg-stone-100 hover:text-stone-600'
                            } ${(!item.panel_size || item.panel_size === 'None') ? 'top-8' : ''}`}
                            title={productNotes[item.id] ? "Ver/editar nota" : "Adicionar nota"}
                            data-testid={`btn-note-${item.id}`}
                          >
                            <Warning size={16} weight={productNotes[item.id] ? "fill" : "regular"} />
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmDelete('product', item.id, item.product?.code); }}
                            className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm transition-colors z-10"
                            data-testid={`btn-remove-${item.id}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="p-3 pb-4 flex flex-col flex-grow">
                          <p className="code-badge text-xs">{item.product.code}</p>
                          {/* Show note preview if exists */}
                          {productNotes[item.id] && (
                            <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1 line-clamp-2">
                              ⚠️ {productNotes[item.id]}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                            {item.product.collection && <span>{item.product.collection}</span>}
                            {item.product.color && <span>• {item.product.color}</span>}
                          </div>
                          {item.product.size && (
                            <p className="text-xs text-stone-400 mt-1">{item.product.size}</p>
                          )}
                          {/* Panel Size Selector and Discontinued Badge - At bottom of card */}
                          <div className="mt-auto pt-3 flex items-center justify-between gap-2 min-h-[32px]">
                            {/* Discontinued badge on the left */}
                            {item.product?.is_discontinued ? (
                              <span className="px-2 py-1 text-xs rounded font-medium bg-stone-800 text-white whitespace-nowrap">
                                Descontinuado
                              </span>
                            ) : (
                              <span></span>
                            )}
                            {/* Panel size selector on the right */}
                            {getSizeOptions(item.panel_size).length > 0 && (
                              <select
                                value={item.panel_size || ""}
                                onChange={(e) => updateProductPanelSize(item.id, e.target.value)}
                                className={`appearance-none px-3 py-1 text-xs rounded font-medium cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                  (!item.panel_size || item.panel_size === 'None')
                                    ? 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 animate-pulse'
                                    : 'bg-[#009392] text-white hover:bg-[#007b7a] focus:ring-[#009392]'
                                }`}
                                data-testid={`panel-size-select-${item.id}`}
                              >
                                {(!item.panel_size || item.panel_size === 'None') && (
                                  <option value="" className="bg-white text-red-500">⚠️ Seleccionar medida</option>
                                )}
                                {getSizeOptions(item.panel_size).map(size => (
                                  <option key={size} value={size} className="bg-white text-stone-800">{size}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="p-4 text-center text-stone-400">
                        Produto não disponível
                      </div>
                    )}
                  </div>
                    )}
                  </SortablePanelCard>
                ))}
                </SortableContext>
                </DndContext>
                </div>
              </>
            )}

            {/* Painéis Fora de Catálogo Section */}
            <div className={`mt-8 pt-8 border-t-2 border-stone-200 ${panelFilter && panelFilter !== 'custom' ? 'hidden' : ''}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                    Painéis Técnicos fora de catalogo Merchandising
                  </h2>
                </div>
                {!isReadOnly && (
                <Button 
                  className="btn-primary" 
                  onClick={() => setShowCustomPanelDialog(true)} 
                  data-testid="btn-add-custom-panel"
                >
                  <Plus size={18} />
                  Adicionar Produtos
                </Button>
                )}
              </div>

              {customPanels.length === 0 ? (
                <div className="empty-state py-8 bg-stone-50 rounded-lg">
                  <ImageSquare size={40} className="text-stone-300 mb-3" />
                  <p className="text-stone-500">Sem painéis fora de catálogo</p>
                </div>
              ) : (
                <div className="product-grid">
                  {customPanels
                    .filter(panel => {
                      if (!panelFilter || panelFilter === 'custom') return true;
                      if (panelFilter === 'no-size') return !panel.panel_size || panel.panel_size === 'None';
                      if (panelFilter.startsWith('size:')) {
                        const size = panelFilter.replace('size:', '');
                        return panel.panel_size === size;
                      }
                      return false; // Hide custom panels when filtering by defect
                    })
                    .map((panel) => (
                    <div key={panel.id} className="product-card flex flex-col" data-testid={`custom-panel-${panel.id}`}>
                      <div className="relative bg-stone-50 p-2">
                        <OptimizedImage 
                          src={resolveImageUrl(panel.image_url)} 
                          alt={panel.name}
                          className="w-full h-auto max-h-64 object-contain mx-auto"
                          width={300}
                        />
                        {!isReadOnly && (
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditCustomPanel(panel); }}
                            className="p-1.5 bg-white/90 hover:bg-blue-50 text-stone-600 hover:text-blue-500 rounded-full shadow-sm transition-colors z-10"
                            data-testid={`btn-edit-custom-panel-${panel.id}`}
                          >
                            <NotePencil size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteCustomPanel(panel.id, panel.name); }}
                            className="p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm transition-colors z-10"
                            data-testid={`btn-delete-custom-panel-${panel.id}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        )}
                        <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                          Descontinuado
                        </span>
                      </div>
                      <div className="p-3 flex flex-col flex-grow">
                        <p className="font-medium text-stone-900 text-sm">{panel.name}</p>
                        {/* Panel Size and Value */}
                        <div className="mt-auto pt-3 flex justify-between items-center">
                          {panel.value > 0 && (
                            <span className="text-sm font-bold text-[#00A19A]">
                              €{parseFloat(panel.value).toFixed(2)}
                            </span>
                          )}
                          {panel.panel_size && (
                            <span className="px-3 py-1 bg-[#009392] text-white text-xs rounded font-medium ml-auto">
                              {panel.panel_size}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Expositores Tab */}
        <TabsContent value="expositores">
          <div className="gresart-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                  Expositores no Showroom
                </h2>
              </div>
              {!isReadOnly && (
              <Button className="btn-primary" onClick={() => openAddDialog("expositor")} data-testid="btn-add-expositores">
                <Plus size={18} />
                Adicionar Expositores
              </Button>
              )}
            </div>

            {expositorProducts.length === 0 ? (
              <div className="empty-state py-12">
                <Package size={48} className="text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem expositores atribuídos</h3>
                <p className="text-stone-500 mb-4">Adicione expositores do catálogo a este showroom</p>
                {!isReadOnly && (
                <Button className="btn-primary" onClick={() => openAddDialog("expositor")} data-testid="btn-add-first-expositor">
                  <Plus size={18} />
                  Adicionar Expositores
                </Button>
                )}
              </div>
            ) : (
              <div className="product-grid">
                {expositorProducts.map((item) => (
                  <div key={item.id} className={`product-card flex flex-col ${item.product?.is_discontinued ? 'ring-2 ring-stone-800 bg-stone-100' : ''}`} data-testid={`expositor-product-${item.id}`}>
                    {item.product ? (
                      <>
                        <div className="relative bg-stone-50 p-2">
                          <OptimizedImage 
                            src={item.product.image_url} 
                            alt={item.product.code}
                            className="w-full h-auto max-h-64 object-contain mx-auto"
                            width={300}
                          />
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmDelete('product', item.id, item.product?.code); }}
                            className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm transition-colors z-10"
                            data-testid={`btn-remove-expositor-${item.id}`}
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="p-3">
                          <p className="code-badge text-xs">{item.product.code}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-stone-500">
                            {item.product.collection && <span>{item.product.collection}</span>}
                            {item.product.color && <span>• {item.product.color}</span>}
                          </div>
                          {item.product.size && (
                            <p className="text-xs text-stone-400 mt-1">{item.product.size}</p>
                          )}
                          {/* Discontinued badge at bottom */}
                          {item.product?.is_discontinued && (
                            <span className="inline-block mt-2 px-2 py-1 text-xs rounded font-medium bg-stone-800 text-white">
                              Descontinuado
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="p-4 text-center text-stone-400">
                        Produto não disponível
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="showroom">
          {/* Showroom Images */}
          <div className="gresart-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                Fotos do Showroom
              </h2>
              <div>
                <input
                  ref={showroomInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="input-showroom-photos"
                  onChange={(e) => handleImageUpload(Array.from(e.target.files), 'showroom')}
                />
                <Button 
                  className="btn-primary" 
                  data-testid="btn-add-showroom-photos"
                  onClick={() => showroomInputRef.current?.click()}
                  disabled={uploadingShowroom}
                >
                  <Plus size={18} />
                  {uploadingShowroom ? "A carregar..." : "Adicionar Fotos"}
                </Button>
              </div>
            </div>

            {(!client.showroom_images || client.showroom_images.length === 0) ? (
              <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-lg">
                <ImageIcon size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500 mb-4">Sem fotos do showroom</p>
                <Button 
                  variant="outline" 
                  onClick={() => showroomInputRef.current?.click()}
                  disabled={uploadingShowroom}
                >
                  <CloudArrowUp size={18} />
                  Carregar Fotos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {client.showroom_images.map((img, idx) => (
                  <div key={`showroom-img-${idx}`} className="relative group rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                    <img 
                      src={resolveImageUrl(img)} 
                      alt={`Showroom ${idx + 1}`} 
                      className="w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity" 
                      onClick={() => setLightboxImage(resolveImageUrl(img))}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmRemoveImage(img, 'showroom'); }}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ambientes">
          {/* Ambiente Images */}
          <div className="gresart-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                Fotos de Ambientes
              </h2>
              <div>
                <input
                  ref={ambienteInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="input-ambiente-photos"
                  onChange={(e) => handleImageUpload(Array.from(e.target.files), 'ambiente')}
                />
                <Button 
                  className="btn-primary" 
                  data-testid="btn-add-ambiente-photos"
                  onClick={() => ambienteInputRef.current?.click()}
                  disabled={uploadingAmbiente}
                >
                  <Plus size={18} />
                  {uploadingAmbiente ? "A carregar..." : "Adicionar Fotos"}
                </Button>
              </div>
            </div>

            {(!client.ambiente_images || client.ambiente_images.length === 0) ? (
              <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-lg">
                <ImageIcon size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500 mb-4">Sem fotos de ambientes</p>
                <Button 
                  variant="outline" 
                  onClick={() => ambienteInputRef.current?.click()}
                  disabled={uploadingAmbiente}
                >
                  <CloudArrowUp size={18} />
                  Carregar Fotos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {client.ambiente_images.map((img, idx) => (
                  <div key={`ambiente-img-${idx}`} className="relative group rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                    <img 
                      src={resolveImageUrl(img)} 
                      alt={`Ambiente ${idx + 1}`} 
                      className="w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxImage(resolveImageUrl(img))}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmRemoveImage(img, 'ambiente'); }}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="projetos">
          {/* Projects Section */}
          <div className="gresart-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                  Projetos
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Organize fotos e documentos por projeto
                </p>
              </div>
              <Button 
                className="btn-primary" 
                onClick={() => setShowProjectDialog(true)}
                data-testid="btn-add-project"
              >
                <Plus size={18} />
                Criar Projeto
              </Button>
            </div>

            {projects.length === 0 ? (
              <div className="empty-state py-12">
                <Folder size={48} className="text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem projetos</h3>
                <p className="text-stone-500 mb-4">Crie um projeto para organizar ficheiros</p>
                <Button className="btn-primary" onClick={() => setShowProjectDialog(true)}>
                  <Plus size={18} />
                  Criar Primeiro Projeto
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map((project) => (
                  <div 
                    key={project.id} 
                    className="border border-stone-200 rounded-lg overflow-hidden"
                    data-testid={`project-${project.id}`}
                  >
                    {/* Project Header */}
                    <div 
                      className="flex items-center justify-between p-4 bg-stone-50 cursor-pointer hover:bg-stone-100 transition-colors"
                      onClick={() => setSelectedProject(selectedProject?.id === project.id ? null : project)}
                    >
                      <div className="flex items-center gap-3">
                        <Folder size={24} className="text-[#00A19A]" weight="duotone" />
                        <div>
                          <h3 className="font-semibold text-stone-900">{project.name}</h3>
                          <p className="text-xs text-stone-500">
                            {project.files?.length || 0} ficheiros • Criado em {new Date(project.created_at).toLocaleDateString('pt-PT')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setSelectedProject(project); setShowProjectFileDialog(true); }}
                          className="text-[#00A19A] hover:text-[#008B85]"
                        >
                          <Plus size={16} />
                          Adicionar
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>

                    {/* Project Files */}
                    {selectedProject?.id === project.id && (
                      <div className="p-4 border-t border-stone-200">
                        {(!project.files || project.files.length === 0) ? (
                          <div className="text-center py-8 text-stone-500">
                            <p className="mb-3">Sem ficheiros neste projeto</p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setShowProjectFileDialog(true)}
                            >
                              <Plus size={16} />
                              Adicionar Ficheiro
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {project.files.map((file) => (
                              <div 
                                key={file.id} 
                                className="relative group rounded-xl overflow-hidden border border-stone-200 bg-stone-50 shadow-sm hover:shadow-md transition-shadow aspect-square"
                              >
                                {file.type === 'image' ? (
                                  <img 
                                    src={resolveImageUrl(file.url)} 
                                    alt={file.name}
                                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                    onClick={() => setLightboxImage(resolveImageUrl(file.url))}
                                  />
                                ) : (
                                  <a 
                                    href={getFileUrl(file.url)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center h-full bg-red-50 hover:bg-red-100 transition-colors"
                                  >
                                    <FilePdf size={48} className="text-red-500 mb-2" weight="duotone" />
                                    <span className="text-xs text-stone-600 px-2 text-center line-clamp-2">{file.name}</span>
                                  </a>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteProjectFile(project.id, file.id); }}
                                  className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-red-50 text-stone-600 hover:text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="historico">
          {/* Showroom Changes History */}
          <div className="gresart-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                  Histórico de Alterações
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Registo de produtos que entraram e saíram do showroom
                </p>
              </div>
              <Button 
                className="btn-primary" 
                onClick={() => setShowChangeDialog(true)}
                data-testid="btn-add-change"
              >
                <Plus size={18} />
                Registar Alteração
              </Button>
            </div>

            {showroomChanges.length === 0 ? (
              <div className="empty-state py-12">
                <ClockCounterClockwise size={48} className="text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem alterações registadas</h3>
                <p className="text-stone-500 mb-4">Registe as alterações ao showroom</p>
                <Button className="btn-primary" onClick={() => setShowChangeDialog(true)}>
                  <Plus size={18} />
                  Registar Alteração
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {showroomChanges.map((change) => (
                  <div 
                    key={change.id} 
                    className="border border-stone-200 rounded-lg p-4"
                    data-testid={`change-${change.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-stone-700">
                          {change.change_date?.split('T')[0]}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditChange(change)}
                          className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <NotePencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteChange(change.id)}
                          className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                    
                    {change.products_added?.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2 text-green-600 text-sm font-medium mb-2">
                          <ArrowUp size={16} />
                          ENTRA PAINEL ({change.products_added.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {change.products_added.map((product) => (
                            <span 
                              key={product.id}
                              className={`inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs ${product.image_url ? 'cursor-pointer hover:bg-green-100 transition-colors' : ''}`}
                              onClick={() => product.image_url && setLightboxImage(product.image_url)}
                              title={product.image_url ? "Clique para ver a imagem" : ""}
                            >
                              {product.image_url && <Eye size={12} />}
                              {product.code}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {change.products_removed?.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2 text-red-600 text-sm font-medium mb-2">
                          <ArrowDown size={16} />
                          SAI PAINEL ({change.products_removed.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {change.products_removed.map((product) => (
                            <span 
                              key={product.id}
                              className={`inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs ${product.image_url ? 'cursor-pointer hover:bg-red-100 transition-colors' : ''}`}
                              onClick={() => product.image_url && setLightboxImage(product.image_url)}
                              title={product.image_url ? "Clique para ver a imagem" : ""}
                            >
                              {product.image_url && <Eye size={12} />}
                              {product.code}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {change.notes && (
                      <p className="text-sm text-stone-500 mt-2 pt-2 border-t border-stone-100">
                        {change.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Catalogs Tab */}
        <TabsContent value="catalogos">
          <div className="gresart-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                  Catálogos Entregues
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Catálogos físicos entregues a este cliente
                </p>
              </div>
              <Button 
                className="btn-primary" 
                onClick={() => setShowCatalogDialog(true)}
                data-testid="btn-add-catalog-delivery"
              >
                <Plus size={18} />
                Registar Entrega
              </Button>
            </div>

            {clientCatalogDeliveries.length === 0 ? (
              <div className="empty-state py-12">
                <BookOpen size={48} className="text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem catálogos registados</h3>
                <p className="text-stone-500 mb-4">Registe as entregas de catálogos físicos a este cliente</p>
                <Button 
                  className="btn-primary" 
                  onClick={() => setShowCatalogDialog(true)}
                  data-testid="btn-add-first-catalog"
                >
                  <Plus size={18} />
                  Registar Primeira Entrega
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {clientCatalogDeliveries.map((delivery) => (
                  <div 
                    key={delivery.id} 
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg border border-stone-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#009392]/10 rounded-lg flex items-center justify-center">
                        <BookOpen size={20} className="text-[#009392]" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{getCatalogName(delivery.catalog_id)}</p>
                        <div className="flex items-center gap-3 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={14} />
                            {new Date(delivery.delivery_date).toLocaleDateString('pt-PT')}
                          </span>
                          <span>•</span>
                          <span>{delivery.quantity} {delivery.quantity === 1 ? 'unidade' : 'unidades'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditDelivery(delivery)}
                        className="p-2 text-stone-400 hover:text-[#00A19A] hover:bg-teal-50 rounded-lg transition-colors"
                        data-testid={`btn-edit-delivery-${delivery.id}`}
                        title="Editar quantidade"
                      >
                        <NotePencil size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteCatalogDelivery(delivery.id)}
                        className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`btn-delete-delivery-${delivery.id}`}
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Visits Tab - viewing is always allowed; add/edit/delete still require leaving read-only mode */}
        <TabsContent value="visitas">
            <div className="gresart-card">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                    Registo de Visitas
                  </h2>
                  <p className="text-sm text-stone-500 mt-1 flex items-center gap-2 flex-wrap" data-testid="visitas-summary">
                    <span>{clientVisits.length} visitas registadas</span>
                    {daysSinceLastVisit !== null && (
                      <span className="text-stone-400" data-testid="dias-desde-ultima-visita">
                        · Última visita há {daysSinceLastVisit === 0 ? 'hoje' : daysSinceLastVisit === 1 ? '1 dia' : `${daysSinceLastVisit} dias`}
                      </span>
                    )}
                  </p>
                </div>
                {!isReadOnly && (
                <Button 
                  className="btn-primary" 
                  onClick={() => {
                    setEditingVisitId(null);
                    setVisitForm({ visit_date: new Date().toISOString().split('T')[0], comments: "" });
                    setShowVisitDialog(true);
                  }}
                  data-testid="btn-add-visit"
                >
                  <Plus size={18} />
                  Nova Visita
                </Button>
                )}
              </div>

              {clientVisits.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarCheck size={48} className="mx-auto text-stone-300 mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">Sem visitas registadas</h3>
                  <p className="text-stone-500">Clique em &quot;Nova Visita&quot; para registar uma visita</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clientVisits.map((visit) => (
                    <div 
                      key={visit.id}
                      className="bg-stone-50 rounded-lg border border-stone-200 p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-[#009392]/10 rounded-lg flex-shrink-0">
                            <CalendarCheck size={20} className="text-[#009392]" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">
                              {new Date(visit.visit_date).toLocaleDateString('pt-PT', { 
                                weekday: 'long',
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                            {visit.comments && (
                              <div className="flex items-start gap-2 mt-2">
                                <ChatText size={16} className="text-stone-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-stone-600">{visit.comments}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        {!isReadOnly && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditVisit(visit)}
                            className="p-1.5 text-stone-400 hover:text-[#009392] hover:bg-stone-100 rounded transition-colors"
                            title="Editar"
                          >
                            <NotePencil size={18} />
                          </button>
                          <button
                            onClick={() => deleteVisit(visit.id)}
                            className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

        <TabsContent value="agendar">
          <div className="gresart-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">Agendar</h2>
                <p className="text-sm text-stone-500 mt-1">
                  {clientTasks.filter(t => t.status === 'pendente').length} tarefas pendentes
                </p>
              </div>
              <Button
                className="btn-primary"
                onClick={() => { setEditingTask(null); setShowTaskDialog(true); }}
                data-testid="btn-add-task"
              >
                <Plus size={18} />
                Nova Tarefa
              </Button>
            </div>

            {clientTasks.length === 0 ? (
              <div className="text-center py-12">
                <CalendarCheck size={48} className="mx-auto text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">Sem tarefas agendadas</h3>
                <p className="text-stone-500">Clique em &quot;Nova Tarefa&quot; para agendar uma ação futura</p>
              </div>
            ) : (
              <div className="space-y-3">
                {clientTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`bg-stone-50 rounded-lg border border-stone-200 p-4 hover:shadow-sm transition-shadow ${task.status === 'concluida' ? 'opacity-60' : ''}`}
                    data-testid={`client-task-row-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-[#009392]/10 rounded-lg flex-shrink-0">
                          <CalendarCheck size={20} className="text-[#009392]" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">
                            {task.task_type}
                            {task.scheduled_time && <span className="text-stone-400 font-normal"> · {task.scheduled_time}</span>}
                          </p>
                          <p className="text-sm text-stone-500">
                            {new Date(task.scheduled_date + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                            {' · '}
                            <span className="capitalize">{task.status}</span>
                          </p>
                          {task.notes && (
                            <div className="flex items-start gap-2 mt-2">
                              <ChatText size={16} className="text-stone-400 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-stone-600">{task.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {task.status !== 'concluida' && (
                          <button
                            onClick={async () => { await axios.patch(`${API}/tasks/${task.id}/complete`); fetchClientTasks(); }}
                            className="p-1.5 text-stone-400 hover:text-green-600 hover:bg-stone-100 rounded transition-colors"
                            title="Concluir"
                            data-testid={`btn-complete-client-task-${task.id}`}
                          >
                            <Check size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => { setEditingTask(task); setShowTaskDialog(true); }}
                          className="p-1.5 text-stone-400 hover:text-[#009392] hover:bg-stone-100 rounded transition-colors"
                          title="Editar"
                          data-testid={`btn-edit-client-task-${task.id}`}
                        >
                          <NotePencil size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteTaskId(task.id)}
                          className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                          data-testid={`btn-delete-client-task-${task.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="encomendas">
          <div className="gresart-card p-0 overflow-hidden">
            <div className="p-6 pb-4">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900 flex items-center gap-2">
                <Truck size={22} className="text-[#009392]" />
                Encomendas Pendentes
              </h2>
            </div>
            {!encomendaInfo ? (
              <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3 mx-6 mb-6" data-testid="client-encomendas-no-data">
                Sem encomendas pendentes carregadas para este cliente. Carregue os PDFs em Perfil &gt; Configurações &gt; Carga de Encomendas Diárias.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-4 px-6 pb-4">
                  <div>
                    <p className="text-xs text-stone-500">Quantidade Pendente</p>
                    <p className="text-lg font-bold text-[#009392]" data-testid="client-encomendas-total-m2">{formatEncomendaNum(encomendaInfo.total_m2)} m²</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Peso Total</p>
                    <p className="text-lg font-bold text-stone-700" data-testid="client-encomendas-total-peso">{formatEncomendaNum(encomendaInfo.total_peso, 0)} kg</p>
                  </div>
                </div>
                <EncomendaOrdersTable items={encomendaInfo.items} idx={0} />
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="vendas">
          <div className="gresart-card mb-4" data-testid="client-sales-summary-2026">
            <h2 className="text-lg font-semibold font-[Manrope] text-stone-900 flex items-center gap-2 mb-3">
              <ChartLineUp size={22} className="text-[#009392]" />
              Resumo de Vendas 2026
            </h2>
            {(() => {
              const faturadoValue = salesInfo?.matched ? (salesInfo.current_sales || 0) : 0;
              const faturadoM2 = salesInfo?.matched ? (salesInfo.total_m2 || 0) : 0;
              const carteiraValue = salesInfo?.pending_value || 0;
              const carteiraM2 = salesInfo?.pending_m2 || 0;
              const totalPotencial = faturadoValue + carteiraValue;
              const totalPotencialM2 = faturadoM2 + carteiraM2;
              const faturadoPct = totalPotencial > 0 ? (faturadoValue / totalPotencial) * 100 : 0;
              const carteiraPct = totalPotencial > 0 ? (carteiraValue / totalPotencial) * 100 : 0;

              if (totalPotencial === 0) {
                return (
                  <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-sales-summary-no-data">
                    Sem dados de vendas ou encomendas pendentes para este cliente ainda.
                  </p>
                );
              }
              return (
                <>
                  <div className="h-4 w-full rounded-full overflow-hidden flex bg-stone-100 mb-4" data-testid="client-sales-summary-bar">
                    <div className="h-full bg-[#009392]" style={{ width: `${faturadoPct}%` }} data-testid="client-sales-summary-bar-faturado" />
                    <div
                      className="h-full"
                      style={{
                        width: `${carteiraPct}%`,
                        backgroundColor: 'rgba(0,147,146,0.3)',
                        backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,147,146,0.55) 0px, rgba(0,147,146,0.55) 4px, transparent 4px, transparent 8px)'
                      }}
                      data-testid="client-sales-summary-bar-carteira"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="p-3 rounded-xl bg-[#009392]/10 border border-[#009392]/20">
                      <p className="text-xs text-stone-500 flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#009392] inline-block flex-shrink-0" />Faturado
                      </p>
                      <p className="text-lg font-bold text-[#009392]" data-testid="client-sales-summary-faturado-value">€{formatCurrency(faturadoValue)}</p>
                      <p className="text-xs text-stone-500">{faturadoM2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                    <div className="p-3 rounded-xl border border-[#009392]/20" style={{ borderStyle: 'dashed', backgroundColor: 'rgba(0,147,146,0.06)' }}>
                      <p className="text-xs text-stone-500 flex items-center gap-1.5">
                        <span
                          className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                          style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,147,146,0.8) 0px, rgba(0,147,146,0.8) 2px, transparent 2px, transparent 4px)' }}
                        />
                        Em Carteira
                      </p>
                      <p className="text-lg font-bold" style={{ color: 'rgba(0,147,146,0.85)' }} data-testid="client-sales-summary-carteira-value">€{formatCurrency(carteiraValue)}</p>
                      <p className="text-xs text-stone-500">{carteiraM2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                    <div className="p-3 rounded-xl bg-stone-100 border border-stone-200 col-span-2 sm:col-span-1">
                      <p className="text-xs text-stone-500">Total Potencial 2026</p>
                      <p className="text-lg font-bold text-stone-800" data-testid="client-sales-summary-total-value">€{formatCurrency(totalPotencial)}</p>
                      <p className="text-xs text-stone-500">{totalPotencialM2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                  </div>
                  {(salesInfo?.stock_health || salesInfo?.acceleration || salesInfo?.is_concentration_risk) && (
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-stone-100" data-testid="client-commercial-signals">
                      {salesInfo?.stock_health && (
                        <span
                          className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 ${
                            salesInfo.stock_health === 'green' ? 'bg-green-50 text-green-700 border border-green-200' :
                            salesInfo.stock_health === 'yellow' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            'bg-red-50 text-red-700 border border-red-200'
                          }`}
                          data-testid="client-signal-stock-health"
                        >
                          <span className={`w-2 h-2 rounded-full inline-block ${
                            salesInfo.stock_health === 'green' ? 'bg-green-500' : salesInfo.stock_health === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                          }`} />
                          Saúde de Stock: {salesInfo.pct_ord_1}% Ord 1º
                        </span>
                      )}
                      {salesInfo?.acceleration === 'up' && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 bg-green-50 text-green-700 border border-green-200" data-testid="client-signal-acceleration-up">
                          <TrendUp size={13} weight="bold" />
                          Crescimento — acima da média mensal
                        </span>
                      )}
                      {salesInfo?.acceleration === 'down' && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 bg-red-50 text-red-700 border border-red-200" data-testid="client-signal-acceleration-down">
                          <TrendDown size={13} weight="bold" />
                          Abrandamento — bem abaixo da média mensal
                        </span>
                      )}
                      {salesInfo?.is_concentration_risk && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200" data-testid="client-signal-concentration-risk">
                          <Scales size={13} weight="fill" />
                          Risco de Concentração: {salesInfo.concentration_pct}% das vendas totais
                        </span>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="gresart-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2
                onClick={() => handleSelectSalesYear('2026')}
                className={`text-lg font-semibold font-[Manrope] flex items-center gap-2 cursor-pointer select-none transition-colors ${selectedSalesYear === '2026' ? 'text-[#009392]' : 'text-stone-900 hover:text-[#009392]'}`}
                data-testid="client-vendas-year-current-title"
              >
                <ChartLineUp size={22} className="text-[#009392]" />
                Vendas 2026
              </h2>
              <div className="flex items-center gap-2" data-testid="client-vendas-year-selector">
                {['2025', '2024'].map(year => {
                  const isActive = selectedSalesYear === year;
                  let variationPct = null;
                  if (year === '2025' && annualPerformance?.matched) {
                    const sales2025 = annualPerformance.years?.['2025']?.sales;
                    const sales2024 = annualPerformance.years?.['2024']?.sales;
                    if (sales2024 > 0 && sales2025 != null) {
                      variationPct = Math.round(((sales2025 - sales2024) / sales2024) * 100);
                    }
                  }
                  const isUp = variationPct !== null && variationPct >= 0;
                  return (
                    <button
                      key={year}
                      type="button"
                      onClick={() => handleSelectSalesYear(year)}
                      data-testid={`client-vendas-year-btn-${year}`}
                      className={`px-3 py-2 rounded-xl border text-sm font-bold transition-colors flex items-center justify-center gap-1.5 w-32 flex-shrink-0 ${
                        isActive
                          ? 'bg-[#009392] border-[#009392] text-white'
                          : 'bg-[#009392]/10 border-[#009392]/20 text-stone-600 hover:bg-[#009392]/20'
                      }`}
                    >
                      {year}
                      {variationPct !== null && (
                        <span
                          className={`font-bold ${isUp ? (isActive ? 'text-emerald-200' : 'text-emerald-600') : (isActive ? 'text-rose-200' : 'text-rose-600')}`}
                          data-testid="client-vendas-year-2025-variation"
                        >
                          {isUp ? '▲' : '▼'}{Math.abs(variationPct)}%
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedSalesYear === '2026' ? (
              !salesInfo || !salesInfo.matched ? (
                <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-vendas-no-data">
                  Sem dados de vendas correspondentes para este cliente ainda. Carregue os ficheiros GPP em "Análise de Vendas".
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-[#009392]/10 border border-[#009392]/20">
                      <p className="text-xs text-stone-500">Vendas Atuais</p>
                      <p className="text-lg font-bold text-[#009392]" data-testid="client-vendas-total">€{formatCurrency(salesInfo.current_sales)}</p>
                      {salesInfo.yoy_change !== null && salesInfo.yoy_change !== undefined && (
                        <p className={`text-xs font-medium mt-0.5 ${salesInfo.yoy_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {salesInfo.yoy_change >= 0 ? '+' : ''}{salesInfo.yoy_change}% vs ano anterior
                        </p>
                      )}
                    </div>
                    <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
                      <p className="text-xs text-stone-500">M2 Vendido</p>
                      <p className="text-lg font-bold text-teal-600" data-testid="client-vendas-m2">{(salesInfo.total_m2 || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                    <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20">
                      <p className="text-xs text-stone-500">Preço Médio</p>
                      <p className="text-lg font-bold text-sky-600" data-testid="client-vendas-avg-price">{salesInfo.avg_price != null ? `€${formatCurrency(salesInfo.avg_price)}/m²` : '—'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <p className="text-xs text-stone-500">Produtos comprados</p>
                      <p className="text-lg font-bold text-purple-600">{salesInfo.top_products?.length || 0}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Vendas por Série</p>
                      <div className="space-y-1.5 h-96 overflow-y-auto pr-1" data-testid="client-vendas-by-series">
                        {(() => {
                          const bySeries = {};
                          (salesInfo.top_products || []).forEach(p => {
                            const key = p.collection ? toSentenceCase(p.collection) : 'Outros';
                            if (!bySeries[key]) bySeries[key] = { sales: 0, m2: 0 };
                            bySeries[key].sales += p.sales || 0;
                            bySeries[key].m2 += p.qty || 0;
                          });
                          const seriesList = Object.entries(bySeries).sort((a, b) => b[1].sales - a[1].sales);
                          const maxSeries = Math.max(...seriesList.map(s => s[1].sales), 1);
                          if (seriesList.length === 0) return <p className="text-xs text-stone-400">Sem dados de série disponíveis.</p>;
                          return seriesList.map(([name, data], idx) => (
                            <div key={name} className="flex items-center gap-2" data-testid={`client-vendas-series-${idx}`}>
                              <span className="text-xs text-stone-600 w-20 truncate flex-shrink-0">{name}</span>
                              <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#009392] rounded-full" style={{ width: `${(data.sales / maxSeries) * 100}%` }} />
                              </div>
                              {data.m2 > 0 && (
                                <span className="text-xs text-teal-600 whitespace-nowrap flex-shrink-0">{data.m2.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span>
                              )}
                              <span className="text-xs font-semibold text-stone-700 w-20 text-right flex-shrink-0">€{formatCurrency(data.sales)}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Vendas por Produtos</p>
                      <div className="space-y-1.5 h-96 overflow-y-auto pr-1" data-testid="client-vendas-products-list">
                        {(salesInfo.top_products || []).map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 py-1.5 px-2.5 bg-stone-50 rounded-lg text-xs">
                            <span className="text-stone-700 truncate" title={p.material}>{cleanProductName(p.material)}</span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {p.qty != null && (
                                <span className="text-teal-600 whitespace-nowrap">{p.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span>
                              )}
                              {p.avg_price != null && (
                                <span className="text-sky-600 whitespace-nowrap">{formatCurrency(p.avg_price)}/m²</span>
                              )}
                              <span className="font-semibold text-[#009392] whitespace-nowrap">€{formatCurrency(p.sales)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )
            ) : (() => {
              const yearData = annualPerformance?.matched ? annualPerformance.years?.[selectedSalesYear] : null;
              if (!yearData) {
                return (
                  <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-vendas-year-no-data">
                    Sem dados de vendas de {selectedSalesYear} correspondentes para este cliente.
                  </p>
                );
              }
              const hasDetail = (yearData.products && yearData.products.length > 0);
              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-[#009392]/10 border border-[#009392]/20">
                      <p className="text-xs text-stone-500">Vendas {selectedSalesYear}</p>
                      <p className="text-lg font-bold text-[#009392]" data-testid="client-vendas-year-total">€{formatCurrency(yearData.sales)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20">
                      <p className="text-xs text-stone-500">M2 Vendido</p>
                      <p className="text-lg font-bold text-teal-600" data-testid="client-vendas-year-m2">{(yearData.m2 || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</p>
                    </div>
                    <div className="p-3 rounded-xl bg-sky-500/10 border border-sky-500/20">
                      <p className="text-xs text-stone-500">Preço Médio</p>
                      <p className="text-lg font-bold text-sky-600" data-testid="client-vendas-year-avg-price">{yearData.avg_price != null ? `€${formatCurrency(yearData.avg_price)}/m²` : '—'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                      <p className="text-xs text-stone-500">Produtos comprados</p>
                      <p className="text-lg font-bold text-purple-600" data-testid="client-vendas-year-products-count">{hasDetail ? yearData.products.length : '—'}</p>
                    </div>
                  </div>
                  {hasDetail ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Vendas por Série</p>
                        <div className="space-y-1.5 h-96 overflow-y-auto pr-1" data-testid="client-vendas-year-by-series">
                          {(() => {
                            const seriesList = yearData.series || [];
                            const maxSeries = Math.max(...seriesList.map(s => s.sales || 0), 1);
                            if (seriesList.length === 0) return <p className="text-xs text-stone-400">Sem dados de série disponíveis.</p>;
                            return seriesList.map((s, idx) => (
                              <div key={s.collection} className="flex items-center gap-2" data-testid={`client-vendas-year-series-${idx}`}>
                                <span className="text-xs text-stone-600 w-20 truncate flex-shrink-0">{s.collection}</span>
                                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#009392] rounded-full" style={{ width: `${((s.sales || 0) / maxSeries) * 100}%` }} />
                                </div>
                                {s.qty > 0 && (
                                  <span className="text-xs text-teal-600 whitespace-nowrap flex-shrink-0">{s.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span>
                                )}
                                <span className="text-xs font-semibold text-stone-700 w-20 text-right flex-shrink-0">€{formatCurrency(s.sales)}</span>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Vendas por Produtos</p>
                        <div className="space-y-1.5 h-96 overflow-y-auto pr-1" data-testid="client-vendas-year-products-list">
                          {yearData.products.map((p, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-2 py-1.5 px-2.5 bg-stone-50 rounded-lg text-xs">
                              <span className="text-stone-700 truncate" title={p.material}>{cleanProductName(p.material)}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {p.qty != null && (
                                  <span className="text-teal-600 whitespace-nowrap">{p.qty.toLocaleString('pt-PT', { maximumFractionDigits: 0 })} m²</span>
                                )}
                                {p.avg_price != null && (
                                  <span className="text-sky-600 whitespace-nowrap">{formatCurrency(p.avg_price)}/m²</span>
                                )}
                                <span className="font-semibold text-[#009392] whitespace-nowrap">€{formatCurrency(p.sales)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-vendas-year-detail-soon">
                      Detalhe por série disponível em breve.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="rappel">
          <div className="gresart-card">
            <h2 className="text-lg font-semibold font-[Manrope] text-stone-900 flex items-center gap-2 mb-4">
              <Percent size={22} className="text-[#009392]" />
              Rappel
            </h2>
            {!rappelInfo ? (
              <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-rappel-no-data">
                Este cliente não corresponde a nenhum distribuidor com objetivos de Rappel configurados.
              </p>
            ) : (
              <RappelClientCard client={rappelInfo} />
            )}
          </div>
        </TabsContent>

        <TabsContent value="reclamacoes">
          <div className="gresart-card">
            <h2 className="text-lg font-semibold font-[Manrope] text-stone-900 flex items-center gap-2 mb-4">
              <Flag size={22} className="text-[#009392]" />
              Reclamações
            </h2>
            {clientComplaints.length === 0 ? (
              <p className="text-sm text-stone-500 bg-stone-50 rounded-lg px-3 py-3" data-testid="client-complaints-empty">
                Este cliente ainda não tem reclamações registadas.
              </p>
            ) : (
              <div className="space-y-2" data-testid="client-complaints-list">
                {clientComplaints.map((c) => {
                  const statusCfg = {
                    pendente: { label: "Pendente", className: "bg-amber-100 text-amber-700 border-amber-200" },
                    em_analise: { label: "Em Análise", className: "bg-sky-100 text-sky-700 border-sky-200" },
                    resolvida: { label: "Resolvida", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
                  }[c.status] || { label: "Pendente", className: "bg-amber-100 text-amber-700 border-amber-200" };
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => navigate(`/reclamacoes/${c.id}`)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-stone-50 hover:bg-stone-100 border border-stone-200 transition-colors text-left"
                      data-testid={`client-complaint-row-${c.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-800 truncate">{c.product_name || "Produto não especificado"}</p>
                        <p className="text-xs text-stone-400">{c.reseller_date || "-"}</p>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium whitespace-nowrap ${statusCfg.className}`}>
                        {statusCfg.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notas">
          {/* Notas Comerciais */}
          <div className="gresart-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                Notas Comerciais
              </h2>
              <Button 
                className="btn-primary" 
                onClick={saveNotes}
                disabled={savingNotes || notes === (client.notas_comerciais || client.notes || "")}
                data-testid="btn-save-notes"
              >
                <FloppyDisk size={18} />
                {savingNotes ? "A guardar..." : "Guardar Notas"}
              </Button>
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas comerciais sobre este cliente... Estas notas aparecerão no relatório."
              className="min-h-[200px] resize-y"
              data-testid="input-notes"
            />
            <p className="text-xs text-stone-400 mt-2">
              As notas serão incluídas no final do relatório gerado.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="eficiencia_exposicao">
          {/* Eficiência de Exposição: cruza as coleções expostas nos Painéis Técnicos de
              catálogo com as coleções realmente vendidas a este cliente (YTD). */}
          <div className="gresart-card" data-testid="eficiencia-exposicao-card">
            <div className="flex items-center gap-2 mb-4">
              <Scales size={20} className="text-[#009392]" />
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">Eficiência de Exposição</h2>
            </div>
            {!exposureEfficiency || (exposureEfficiency.espaco_ineficiente.length === 0 && exposureEfficiency.oportunidade_exposicao.length === 0) ? (
              <p className="text-sm text-stone-400 text-center py-8">
                {exposureEfficiency && !exposureEfficiency.matched_sales
                  ? 'Sem correspondência de vendas para este cliente - ainda não é possível calcular esta métrica.'
                  : 'Sem alertas neste momento - os painéis expostos correspondem bem ao que este cliente compra.'}
              </p>
            ) : (
              <>
                {!exposureEfficiency.matched_sales && (
                  <p className="text-xs text-stone-400 mb-3">Sem correspondência de vendas para este cliente - a lista abaixo mostra apenas o que está exposto.</p>
                )}
                <div className="space-y-3">
                  {exposureEfficiency.espaco_ineficiente.map((item, idx) => (
                    <div key={`ineficiente-${idx}`} className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-100" data-testid={`espaco-ineficiente-${idx}`}>
                      <Warning size={18} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-700">Espaço Ineficiente: {item.collection}</p>
                        <p className="text-xs text-red-500">
                          {item.panel_count} painel{item.panel_count > 1 ? 'éis' : ''} exposto{item.panel_count > 1 ? 's' : ''} desta coleção, mas sem vendas registadas este ano a este cliente.
                        </p>
                      </div>
                    </div>
                  ))}
                  {exposureEfficiency.oportunidade_exposicao.map((item, idx) => (
                    <div key={`oportunidade-${idx}`} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid={`oportunidade-exposicao-${idx}`}>
                      <Lightbulb size={18} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-700">Oportunidade de Exposição: {item.collection}</p>
                        <p className="text-xs text-amber-600">
                          Representa {item.pct_of_total}% das vendas deste cliente ({formatCurrency(item.sales)}), mas não tem nenhum painel exposto.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notas_exposicao">
          {/* Notas de Exposição (Showroom) */}
          <div className="gresart-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold font-[Manrope] text-stone-900">
                Notas de Exposição
              </h2>
              <Button 
                className="btn-primary" 
                onClick={saveNotasExposicao}
                disabled={savingNotasExposicao || notasExposicao === (client.notas_exposicao || "")}
                data-testid="btn-save-notas-exposicao"
              >
                <FloppyDisk size={18} />
                {savingNotasExposicao ? "A guardar..." : "Guardar Notas"}
              </Button>
            </div>
            <Textarea
              value={notasExposicao}
              onChange={(e) => setNotasExposicao(e.target.value)}
              placeholder="Adicione notas específicas do showroom/exposição deste cliente..."
              className="min-h-[200px] resize-y"
              data-testid="input-notas-exposicao"
            />
          </div>
        </TabsContent>
        </>
        )}
      </Tabs>
      </div>

      {/* Zona de Contacto Rápido: telefone / email / web / redes sociais, centrado, minimalista.
          Telefone e email são específicos de cada sucursal; website e redes sociais herdam
          da matriz quando a sucursal não tem valor próprio (mesma marca/site em todas as lojas). */}
      {(() => {
        const displayWebsite = client.website || branches[0]?.matriz_website;
        const displaySocial = client.social_media || branches[0]?.matriz_social_media;
        const displayPhone = client.contacts?.[0]?.phone || client.phone;
        return (client.email || displayPhone || displayWebsite || displaySocial) && (
          <div className="relative z-10 mt-auto pt-8 pb-2 border-t border-stone-100 flex flex-col items-center gap-2" data-testid="client-footer-info">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-stone-500">
              {displayPhone && (
                <a href={`tel:${displayPhone.replace(/\s+/g, '')}`} className="flex items-center gap-2 hover:text-[#009392] transition-colors" data-testid="client-footer-phone">
                  <Phone size={16} />
                  {displayPhone}
                </a>
              )}
              {client.email && (
                <a href={`mailto:${client.email}`} className="flex items-center gap-2 hover:text-[#009392] transition-colors" data-testid="client-footer-email">
                  <EnvelopeSimple size={16} />
                  {client.email}
                </a>
              )}
              {displayWebsite && (
                <a href={displayWebsite.startsWith('http') ? displayWebsite : `https://${displayWebsite}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#009392] transition-colors" data-testid="client-footer-website">
                  <Globe size={16} />
                  {displayWebsite}
                </a>
              )}
              {displaySocial && (
                <a
                  href={displaySocial.trim().startsWith('http') ? displaySocial.trim() : `https://instagram.com/${displaySocial.trim().replace(/^@/, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#009392] transition-colors"
                  data-testid="client-footer-social"
                >
                  <InstagramLogo size={16} />
                  {displaySocial}
                </a>
              )}
            </div>
          </div>
        );
      })()}

      {/* Add Products Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-[Manrope]">
              {addDialogType === "expositor" ? "Adicionar Expositores ao Showroom" : "Adicionar Produtos ao Showroom"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="search-input-container mb-4 flex-shrink-0">
            <MagnifyingGlass size={18} />
            <Input
              placeholder="Pesquisar por código, coleção..."
              value={catalogSearch}
              onChange={handleCatalogSearch}
              className="gresart-input pl-10"
              data-testid="search-catalog-products"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 pr-2">
            {catalogProducts.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                {catalogSearch ? "Nenhum produto encontrado" : "Não há produtos no catálogo"}
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {catalogProducts.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  // For expositores, allow adding multiple times (don't mark as "already in showroom")
                  const isInShowroom = addDialogType === "expositor" 
                    ? false 
                    : showroomPainelProductIds.includes(product.id);
                  
                  return (
                    <div
                      key={product.id}
                      onClick={() => !isInShowroom && toggleProductSelection(product.id)}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        isInShowroom 
                          ? "opacity-50 cursor-not-allowed border-stone-200" 
                          : isSelected 
                            ? "border-[#00A19A] ring-2 ring-[#00A19A]/20" 
                            : "border-transparent hover:border-stone-300"
                      }`}
                      data-testid={`catalog-product-${product.id}`}
                    >
                      <div className="bg-stone-50 p-2">
                        <img 
                          src={product.image_url} 
                          alt={product.code}
                          className="w-full h-auto max-h-40 object-contain mx-auto"
                        />
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-[#00A19A] rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                      {isInShowroom && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                          <span className="text-xs bg-stone-800 text-white px-2 py-1 rounded">Já adicionado</span>
                        </div>
                      )}
                      <div className="p-2 flex items-center justify-between gap-1">
                        <p className="text-xs font-mono text-stone-700 truncate">{product.code}</p>
                        {product.value != null && (
                          <p className="text-xs font-semibold text-[#00A19A] flex-shrink-0" data-testid={`catalog-product-price-${product.id}`}>
                            €{parseFloat(product.value).toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-stone-100 flex-shrink-0 bg-white">
            <div>
              <p className="text-sm text-stone-500">
                {selectedProducts.length} produto(s) selecionado(s)
              </p>
              {selectedProducts.length > 0 && (() => {
                const total = catalogProducts
                  .filter(p => selectedProducts.includes(p.id) && p.value != null)
                  .reduce((sum, p) => sum + parseFloat(p.value), 0);
                return total > 0 ? (
                  <p className="text-sm font-semibold text-[#00A19A]" data-testid="selected-products-total">
                    Total: €{total.toFixed(2)}
                  </p>
                ) : null;
              })()}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button 
                className="btn-primary" 
                onClick={addSelectedProducts}
                disabled={selectedProducts.length === 0 || addingProducts}
                data-testid="btn-confirm-add-products"
              >
                {addingProducts ? "A adicionar..." : "Adicionar Selecionados"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Showroom Change Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={(open) => {
        setShowChangeDialog(open);
        if (!open) {
          setEditingChangeId(null);
          setAddedSearch("");
          setRemovedSearch("");
          setChangeForm({
            change_date: new Date().toISOString().split('T')[0],
            products_added: [],
            products_removed: [],
            notes: ""
          });
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingChangeId ? "Editar Alteração" : "Registar Alteração no Showroom"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 py-4">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">
                Data da Alteração
              </label>
              <Input
                type="date"
                value={changeForm.change_date}
                onChange={(e) => setChangeForm({...changeForm, change_date: e.target.value})}
                data-testid="input-change-date"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block flex items-center gap-2">
                <ArrowUp size={16} className="text-green-600" />
                ENTRA PAINEL
              </label>
              <Input
                placeholder="Pesquisar produto por código..."
                value={addedSearch}
                onChange={(e) => setAddedSearch(e.target.value)}
                className="mb-2"
                data-testid="search-products-added"
              />
              <Select 
                value="" 
                onValueChange={(v) => {
                  if (v && !changeForm.products_added.includes(v)) {
                    setChangeForm({...changeForm, products_added: [...changeForm.products_added, v]});
                    setAddedSearch("");
                  }
                }}
              >
                <SelectTrigger data-testid="select-products-added">
                  <SelectValue placeholder="Selecione produtos..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogProducts
                    .filter(p => !changeForm.products_added.includes(p.id))
                    .filter(p => !addedSearch || p.code?.toLowerCase().includes(addedSearch.toLowerCase()) || p.collection?.toLowerCase().includes(addedSearch.toLowerCase()))
                    .slice(0, 50)
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.code} - {p.collection || 'Sem coleção'}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {changeForm.products_added.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {changeForm.products_added.map(pid => {
                    const product = catalogProducts.find(p => p.id === pid);
                    return (
                      <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs">
                        {product?.code || pid}
                        <button onClick={() => toggleProductInChange(pid, 'added')} className="hover:text-green-900">
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block flex items-center gap-2">
                <ArrowDown size={16} className="text-red-600" />
                SAI PAINEL
              </label>
              <Input
                placeholder="Pesquisar produto por código..."
                value={removedSearch}
                onChange={(e) => setRemovedSearch(e.target.value)}
                className="mb-2"
                data-testid="search-products-removed"
              />
              <Select 
                value="" 
                onValueChange={(v) => {
                  if (v && !changeForm.products_removed.includes(v)) {
                    setChangeForm({...changeForm, products_removed: [...changeForm.products_removed, v]});
                    setRemovedSearch("");
                  }
                }}
              >
                <SelectTrigger data-testid="select-products-removed">
                  <SelectValue placeholder="Selecione produtos do showroom..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Showroom products */}
                  {showroomProducts
                    .filter(sp => !changeForm.products_removed.includes(sp.product?.id || sp.product_id))
                    .filter(sp => {
                      const code = sp.product?.code || '';
                      const collection = sp.product?.collection || '';
                      return !removedSearch || code.toLowerCase().includes(removedSearch.toLowerCase()) || collection.toLowerCase().includes(removedSearch.toLowerCase());
                    })
                    .map((sp) => (
                      <SelectItem key={sp.product?.id || sp.product_id} value={sp.product?.id || sp.product_id}>
                        {sp.product?.code || 'Sem código'} - {sp.product?.collection || 'Sem coleção'}
                      </SelectItem>
                    ))}
                  {/* Custom panels (Descontinuados) */}
                  {customPanels
                    .filter(cp => !changeForm.products_removed.includes(`custom_${cp.id}`))
                    .filter(cp => !removedSearch || cp.name?.toLowerCase().includes(removedSearch.toLowerCase()))
                    .map((cp) => (
                      <SelectItem key={`custom_${cp.id}`} value={`custom_${cp.id}`}>
                        {cp.name} - DESCONTINUADO
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {changeForm.products_removed.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {changeForm.products_removed.map(pid => {
                    // Check if it's a custom panel
                    if (pid.startsWith('custom_')) {
                      const customPanel = customPanels.find(cp => `custom_${cp.id}` === pid);
                      return (
                        <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs">
                          {customPanel?.name || pid} (F.C.)
                          <button onClick={() => toggleProductInChange(pid, 'removed')} className="hover:text-red-900">
                            <X size={12} />
                          </button>
                        </span>
                      );
                    }
                    const showroomItem = showroomProducts.find(sp => (sp.product?.id || sp.product_id) === pid);
                    const product = showroomItem?.product || catalogProducts.find(p => p.id === pid);
                    return (
                      <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs">
                        {product?.code || pid}
                        <button onClick={() => toggleProductInChange(pid, 'removed')} className="hover:text-red-900">
                          <X size={12} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">
                Notas (opcional)
              </label>
              <Textarea
                value={changeForm.notes}
                onChange={(e) => setChangeForm({...changeForm, notes: e.target.value})}
                placeholder="Observações sobre esta alteração..."
                className="min-h-[80px]"
                data-testid="input-change-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowChangeDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleAddChange}
              disabled={changeForm.products_added.length === 0 && changeForm.products_removed.length === 0}
              data-testid="btn-save-change"
            >
              <Check size={18} />
              {editingChangeId ? "Guardar" : "Registar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Catalog Delivery Dialog */}
      <Dialog open={showCatalogDialog} onOpenChange={(open) => {
        setShowCatalogDialog(open);
        if (!open) {
          setEditingDeliveryId(null);
          setCatalogDeliveryForm({
            catalog_id: "",
            quantity: 1,
            delivery_date: new Date().toISOString().split('T')[0]
          });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingDeliveryId ? "Editar Entrega de Catálogo" : "Registar Entrega de Catálogo"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="catalog-select">Catálogo</Label>
              <Select
                value={catalogDeliveryForm.catalog_id}
                onValueChange={(value) => setCatalogDeliveryForm(prev => ({ ...prev, catalog_id: value }))}
                disabled={!!editingDeliveryId}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Selecione um catálogo" />
                </SelectTrigger>
                <SelectContent>
                  {availableCatalogs.map((catalog) => (
                    <SelectItem key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="quantity">Quantidade</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={catalogDeliveryForm.quantity}
                onChange={(e) => setCatalogDeliveryForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="delivery-date">Data de Entrega</Label>
              <Input
                id="delivery-date"
                type="date"
                value={catalogDeliveryForm.delivery_date}
                onChange={(e) => setCatalogDeliveryForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCatalogDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={handleAddCatalogDelivery}
              disabled={savingCatalogDelivery || !catalogDeliveryForm.catalog_id}
            >
              {savingCatalogDelivery ? "A guardar..." : (editingDeliveryId ? "Atualizar" : "Registar Entrega")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Warning size={20} className="text-amber-500" />
              Nota do Painel
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Ex: Peça rota, calcomania despegada, necessita limpeza..."
              className="min-h-[120px]"
              data-testid="input-product-note"
            />
            <p className="text-xs text-stone-500 mt-2">
              Deixe em branco para remover a nota
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={saveProductNote}
              disabled={savingNote}
            >
              {savingNote ? "A guardar..." : "Guardar Nota"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Panel Dialog - Multiple Upload */}
      <Dialog open={showCustomPanelDialog} onOpenChange={(open) => {
        setShowCustomPanelDialog(open);
        if (!open) {
          setPendingCustomPanels([]);
          setCustomPanelForm({ name: "", image_url: "" });
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Adicionar Painéis Fora de Catálogo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Upload Area */}
            <div>
              <Label>Carregar Imagens</Label>
              <input
                ref={customPanelInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleCustomPanelUpload}
                className="hidden"
              />
              
              <button
                onClick={() => customPanelInputRef.current?.click()}
                disabled={uploadingCustomPanel}
                className="mt-2 w-full h-28 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#00A19A] hover:bg-teal-50/50 transition-colors disabled:opacity-50"
              >
                {uploadingCustomPanel ? (
                  <>
                    <LoadingSpinner size={24} />
                    <span className="text-sm text-stone-500">A carregar...</span>
                  </>
                ) : (
                  <>
                    <CloudArrowUp size={32} className="text-stone-400" />
                    <span className="text-sm text-stone-500">Clique para selecionar imagens</span>
                    <span className="text-xs text-stone-400">Múltiplas imagens permitidas</span>
                  </>
                )}
              </button>
            </div>

            {/* Pending Panels List */}
            {pendingCustomPanels.length > 0 && (
              <div className="space-y-3">
                <Label>Painéis para adicionar ({pendingCustomPanels.length})</Label>
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {pendingCustomPanels.map((panel, index) => (
                    <div 
                      key={panel.tempId} 
                      className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200"
                    >
                      <img 
                        src={resolveImageUrl(panel.image_url)} 
                        alt={panel.name}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0 cursor-pointer hover:opacity-80"
                        onClick={() => setLightboxImage(resolveImageUrl(panel.image_url))}
                      />
                      <div className="flex-1 min-w-0 space-y-2">
                        <Input
                          value={panel.name}
                          onChange={(e) => updatePendingPanelName(panel.tempId, e.target.value)}
                          placeholder="Nome do painel"
                          data-testid={`input-pending-panel-${index}`}
                        />
                        {getSizeOptions(panel.panel_size).length > 0 && (
                          <select
                            value={panel.panel_size || ""}
                            onChange={(e) => updatePendingPanelSize(panel.tempId, e.target.value)}
                            className="w-full text-sm px-2 py-1.5 border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#009392]"
                            data-testid={`select-pending-panel-size-${index}`}
                          >
                            <option value="">Selecionar medida</option>
                            {getSizeOptions(panel.panel_size).map(size => (
                              <option key={size} value={size}>{size}</option>
                            ))}
                          </select>
                        )}
                      </div>
                      <button
                        onClick={() => removePendingPanel(panel.tempId)}
                        className="p-1.5 hover:bg-red-50 text-stone-400 hover:text-red-500 rounded-full transition-colors flex-shrink-0"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingCustomPanels.length === 0 && (
              <p className="text-sm text-stone-500 text-center py-4">
                Selecione uma ou mais imagens para adicionar como painéis fora de catálogo
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => {
              setShowCustomPanelDialog(false);
              setPendingCustomPanels([]);
              setCustomPanelForm({ name: "", image_url: "" });
            }}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={saveAllPendingPanels}
              disabled={pendingCustomPanels.length === 0 || uploadingCustomPanel}
            >
              {uploadingCustomPanel ? "A guardar..." : `Adicionar ${pendingCustomPanels.length} Painel(is)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Custom Panel Dialog */}
      <Dialog open={!!editingCustomPanel} onOpenChange={(open) => !open && setEditingCustomPanel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <NotePencil size={20} className="text-[#00A19A]" />
              Editar Painel
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {editingCustomPanel?.image_url && (
              <div className="flex justify-center">
                <img 
                  src={resolveImageUrl(editingCustomPanel.image_url)} 
                  alt={editingCustomPanel.name}
                  className="h-32 w-auto object-contain rounded-lg border border-stone-200"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="edit-panel-name">Nome do Painel</Label>
              <Input
                id="edit-panel-name"
                value={editCustomPanelForm.name}
                onChange={(e) => setEditCustomPanelForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nome do painel"
                className="mt-1.5"
                data-testid="input-edit-panel-name"
              />
            </div>

            {getSizeOptions(editCustomPanelForm.panel_size).length > 0 && (
              <div>
                <Label htmlFor="edit-panel-size">Medida</Label>
                <select
                  id="edit-panel-size"
                  value={editCustomPanelForm.panel_size || ""}
                  onChange={(e) => setEditCustomPanelForm(prev => ({ ...prev, panel_size: e.target.value }))}
                  className="mt-1.5 w-full px-3 py-2 border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#009392]"
                  data-testid="select-edit-panel-size"
                >
                  <option value="">Sem medida</option>
                  {getSizeOptions(editCustomPanelForm.panel_size).map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label htmlFor="edit-panel-value">Valor (€)</Label>
              <Input
                id="edit-panel-value"
                type="number"
                step="0.01"
                min="0"
                value={editCustomPanelForm.value}
                onChange={(e) => setEditCustomPanelForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder="0.00"
                className="mt-1.5"
                data-testid="input-edit-panel-value"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditingCustomPanel(null)}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={saveEditCustomPanel}
              disabled={!editCustomPanelForm.name.trim()}
            >
              Guardar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Visit Dialog */}
      <Dialog open={showVisitDialog} onOpenChange={(open) => {
        if (!open) {
          setShowVisitDialog(false);
          setEditingVisitId(null);
          setVisitForm({ visit_date: new Date().toISOString().split('T')[0], comments: "" });
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <CalendarCheck size={20} className="text-[#00A19A]" />
              {editingVisitId ? "Editar Visita" : "Registar Nova Visita"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="visit-date">Data da Visita</Label>
              <Input
                id="visit-date"
                type="date"
                value={visitForm.visit_date}
                onChange={(e) => setVisitForm(prev => ({ ...prev, visit_date: e.target.value }))}
                className="mt-1.5"
                data-testid="input-visit-date"
              />
            </div>
            <div>
              <Label htmlFor="visit-comments">Comentários</Label>
              <Textarea
                id="visit-comments"
                value={visitForm.comments}
                onChange={(e) => setVisitForm(prev => ({ ...prev, comments: e.target.value }))}
                placeholder="Observações sobre a visita..."
                className="mt-1.5 min-h-[100px]"
                data-testid="input-visit-comments"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setShowVisitDialog(false);
              setEditingVisitId(null);
              setVisitForm({ visit_date: new Date().toISOString().split('T')[0], comments: "" });
            }}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={saveVisit}
              disabled={!visitForm.visit_date || savingVisit}
            >
              {savingVisit ? "A guardar..." : (editingVisitId ? "Atualizar" : "Registar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TaskFormDialog
        open={showTaskDialog}
        onClose={() => { setShowTaskDialog(false); setEditingTask(null); }}
        onSaved={fetchClientTasks}
        task={editingTask}
        defaultEntity={{ id: clientId, name: client?.name, type: "client" }}
      />

      <ConfirmDeleteDialog
        open={!!deleteTaskId}
        onOpenChange={(v) => !v && setDeleteTaskId(null)}
        onConfirm={async () => {
          await axios.delete(`${API}/tasks/${deleteTaskId}`);
          setDeleteTaskId(null);
          fetchClientTasks();
        }}
        itemType="tarefa"
      />


      {/* Create Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Folder size={20} className="text-[#00A19A]" />
              Criar Novo Projeto
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="project-name">Nome do Projeto</Label>
              <Input
                id="project-name"
                value={projectForm.name}
                onChange={(e) => setProjectForm({ name: e.target.value })}
                placeholder="Ex: Renovação Showroom 2024"
                className="mt-1.5"
                data-testid="input-project-name"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setShowProjectDialog(false);
              setProjectForm({ name: "" });
            }}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={createProject}
              disabled={!projectForm.name.trim() || savingProject}
            >
              {savingProject ? "A criar..." : "Criar Projeto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Project File Dialog */}
      <Dialog open={showProjectFileDialog} onOpenChange={setShowProjectFileDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Plus size={20} className="text-[#00A19A]" />
              Adicionar Ficheiro ao Projeto
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-stone-500 mb-4">
              Adicione imagens ou PDFs ao projeto &quot;{selectedProject?.name}&quot;
            </p>
            
            <input
              ref={projectFileInputRef}
              type="file"
              accept="image/*,.pdf"
              multiple
              onChange={handleProjectFileUpload}
              className="hidden"
            />
            
            <button
              onClick={() => projectFileInputRef.current?.click()}
              disabled={uploadingProjectFile}
              className="w-full h-40 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#00A19A] hover:bg-teal-50/50 transition-colors disabled:opacity-50"
            >
              {uploadingProjectFile ? (
                <>
                  <LoadingSpinner size={32} />
                  <span className="text-sm text-stone-500">A carregar...</span>
                </>
              ) : (
                <>
                  <CloudArrowUp size={40} className="text-stone-400" />
                  <span className="text-sm text-stone-500">Clique para selecionar ficheiros</span>
                  <span className="text-xs text-stone-400">Imagens ou PDFs (múltiplos permitidos)</span>
                </>
              )}
            </button>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowProjectFileDialog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox for viewing images in full size with zoom and pan */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => { setLightboxImage(null); setZoomLevel(1); setPanPosition({ x: 0, y: 0 }); }}
          onWheel={(e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.2 : 0.2;
            setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.5), 5));
          }}
        >
          <button
            onClick={() => { setLightboxImage(null); setZoomLevel(1); setPanPosition({ x: 0, y: 0 }); }}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors z-10"
          >
            <X size={24} />
          </button>
          
          {/* Zoom controls */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 flex items-center gap-2 bg-white/10 backdrop-blur rounded-full p-1">
            <button
              onClick={(e) => { e.stopPropagation(); setZoomLevel(prev => Math.max(prev - 0.5, 0.5)); setPanPosition({ x: 0, y: 0 }); }}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              disabled={zoomLevel <= 0.5}
            >
              <MagnifyingGlassMinus size={20} className="text-white" />
            </button>
            <span className="text-white text-sm px-3 min-w-[60px] text-center">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomLevel(prev => Math.min(prev + 0.5, 5)); }}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              disabled={zoomLevel >= 5}
            >
              <MagnifyingGlassPlus size={20} className="text-white" />
            </button>
            {/* Reset button */}
            <button
              onClick={(e) => { e.stopPropagation(); setZoomLevel(1); setPanPosition({ x: 0, y: 0 }); }}
              className="p-2 hover:bg-white/20 rounded-full transition-colors ml-2 text-white text-xs"
              title="Reset"
            >
              100%
            </button>
          </div>
          
          {/* Hint for scrolling */}
          <div className="absolute top-4 left-4 text-white/50 text-xs">
            Scroll para zoom • Arrastra para mover
          </div>
          
          <div 
            className="overflow-hidden max-w-full max-h-[85vh] flex items-center justify-center cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => {
              if (zoomLevel > 1) {
                e.preventDefault();
                setIsDragging(true);
                setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
              }
            }}
            onMouseMove={(e) => {
              if (isDragging && zoomLevel > 1) {
                setPanPosition({
                  x: e.clientX - dragStart.x,
                  y: e.clientY - dragStart.y
                });
              }
            }}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage} 
              alt="Full size"
              className="object-contain select-none"
              style={{ 
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                maxHeight: '85vh',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
              draggable={false}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, id: null, name: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] text-red-600 flex items-center gap-2">
              <Trash size={20} />
              Confirmar Eliminação
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-stone-600">
              Tem certeza que deseja eliminar {deleteConfirm.type === 'customPanel' ? 'este painel' : 'este produto do showroom'}?
            </p>
            {deleteConfirm.name && (
              <p className="mt-2 font-medium text-stone-900 bg-stone-100 px-3 py-2 rounded">
                {deleteConfirm.name}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, type: null, id: null, name: null })}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={imageDeleteConfirm.open}
        onOpenChange={(open) => !open && setImageDeleteConfirm({ open: false, imageUrl: null, imageType: null, previewUrl: null })}
        onConfirm={handleConfirmImageDelete}
        title="Eliminar Imagem"
        itemType="imagem"
        itemName={imageDeleteConfirm.imageType === 'showroom' ? 'do Showroom' : 'do Ambiente'}
        showPreview={true}
        previewUrl={imageDeleteConfirm.previewUrl}
      />

      {/* Work Order (Ordem de Trabalho) Dialog */}
      <Dialog open={showWorkOrderDialog} onOpenChange={setShowWorkOrderDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle className="font-[Manrope] text-amber-600 flex items-center gap-2">
                <NotePencil size={24} />
                Ordem de Trabalho - {client?.name}
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={resetWorkOrder}
                className="text-stone-500 hover:text-red-600 flex-shrink-0"
                data-testid="btn-reset-work-order"
              >
                <ClockCounterClockwise size={16} className="mr-1.5" /> Reiniciar
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Panels Leaving (Saem) */}
            <div className="border border-red-200 rounded-lg p-4 bg-red-50/50">
              <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                <ArrowUp size={18} />
                Painéis que SAEM do Showroom
              </h3>
              <p className="text-xs text-red-600 mb-3">Selecione os painéis que serão retirados</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                {/* Showroom Products */}
                {painelProducts.map(sp => (
                  <label 
                    key={sp.id} 
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                      workOrderPanelsOut.includes(sp.id) 
                        ? 'border-red-500 bg-red-100' 
                        : 'border-stone-200 bg-white hover:border-red-300'
                    }`}
                  >
                    <input 
                      type="checkbox"
                      checked={workOrderPanelsOut.includes(sp.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWorkOrderPanelsOut([...workOrderPanelsOut, sp.id]);
                        } else {
                          setWorkOrderPanelsOut(workOrderPanelsOut.filter(id => id !== sp.id));
                        }
                      }}
                      data-testid={`wo-panel-out-${sp.id}`}
                      className="w-4 h-4 text-red-500 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      {sp.product?.image_url && (
                        <img 
                          src={resolveImageUrl(sp.product.image_url)} 
                          alt="" 
                          className="w-14 h-14 object-contain bg-white rounded mb-1 border border-stone-100"
                        />
                      )}
                      <p className="text-xs font-mono text-stone-700 truncate">
                        {sp.product?.code || 'N/A'}{sp.product?.collection ? ` - ${sp.product.collection}` : ''}
                      </p>
                      {sp.panel_size && <p className="text-[10px] text-stone-500">{sp.panel_size}</p>}
                    </div>
                  </label>
                ))}
                {/* Custom Panels */}
                {customPanels.map(cp => (
                  <label 
                    key={`custom-${cp.id}`} 
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                      workOrderPanelsOut.includes(`custom-${cp.id}`) 
                        ? 'border-red-500 bg-red-100' 
                        : 'border-purple-200 bg-purple-50 hover:border-red-300'
                    }`}
                  >
                    <input 
                      type="checkbox"
                      checked={workOrderPanelsOut.includes(`custom-${cp.id}`)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWorkOrderPanelsOut([...workOrderPanelsOut, `custom-${cp.id}`]);
                        } else {
                          setWorkOrderPanelsOut(workOrderPanelsOut.filter(id => id !== `custom-${cp.id}`));
                        }
                      }}
                      className="w-4 h-4 text-red-500 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      {cp.image_url && (
                        <img 
                          src={resolveImageUrl(cp.image_url)} 
                          alt="" 
                          className="w-14 h-14 object-contain bg-white rounded mb-1 border border-stone-100"
                        />
                      )}
                      <p className="text-xs font-medium text-purple-700 truncate">{cp.name}</p>
                      <p className="text-[10px] text-purple-500">Fora Catálogo</p>
                    </div>
                  </label>
                ))}
              </div>
              {workOrderPanelsOut.length > 0 && (
                <p className="text-sm text-red-600 mt-2 font-medium">{workOrderPanelsOut.length} painel(s) selecionado(s)</p>
              )}
            </div>

            {/* Panels Entering (Entram) */}
            <div className="border border-green-200 rounded-lg p-4 bg-green-50/50">
              <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                <ArrowDown size={18} />
                Painéis que ENTRAM no Showroom
              </h3>
              <p className="text-xs text-green-600 mb-3">Selecione os painéis do catálogo PPM que serão instalados</p>
              
              {/* Search */}
              <div className="relative mb-3">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <Input
                  type="text"
                  placeholder="Pesquisar por código..."
                  value={workOrderSearchIn}
                  onChange={(e) => setWorkOrderSearchIn(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                {catalogProducts
                  .filter(p => !workOrderSearchIn || p.code?.toLowerCase().includes(workOrderSearchIn.toLowerCase()))
                  .slice(0, 50)
                  .map(product => (
                    <label 
                      key={product.id} 
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                        workOrderPanelsIn.includes(product.id) 
                          ? 'border-green-500 bg-green-100' 
                          : 'border-stone-200 bg-white hover:border-green-300'
                      }`}
                    >
                      <input 
                        type="checkbox"
                        checked={workOrderPanelsIn.includes(product.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWorkOrderPanelsIn([...workOrderPanelsIn, product.id]);
                          } else {
                            setWorkOrderPanelsIn(workOrderPanelsIn.filter(id => id !== product.id));
                          }
                        }}
                        className="w-4 h-4 text-green-500 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        {product.image_url && (
                          <img 
                            src={resolveImageUrl(product.image_url)} 
                            alt="" 
                            className="w-14 h-14 object-contain bg-white rounded mb-1 border border-stone-100"
                          />
                        )}
                        <p className="text-xs font-mono text-stone-700 truncate">
                          {product.code}{product.collection ? ` - ${product.collection}` : ''}
                        </p>
                        {product.size && <p className="text-[10px] text-stone-500">{product.size}</p>}
                      </div>
                    </label>
                  ))}
              </div>
              {workOrderPanelsIn.length > 0 && (
                <p className="text-sm text-green-600 mt-2 font-medium">{workOrderPanelsIn.length} painel(s) selecionado(s)</p>
              )}
            </div>

            {/* Observations */}
            <div className="border border-stone-200 rounded-lg p-4">
              <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2">
                <FileText size={18} />
                Observações
              </h3>
              <Textarea
                value={workOrderObservations}
                onChange={(e) => setWorkOrderObservations(e.target.value)}
                placeholder="Instruções adicionais para a equipa de merchandising..."
                className="min-h-[100px]"
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowWorkOrderDialog(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                if (workOrderPanelsOut.length === 0 && workOrderPanelsIn.length === 0) {
                  toast.error("Selecione pelo menos um painel");
                  return;
                }
                setGeneratingWorkOrder(true);
                try {
                  const response = await axios.post(
                    `${API}/clients/${clientId}/work-order/pdf`,
                    {
                      panels_out: workOrderPanelsOut,
                      panels_in: workOrderPanelsIn,
                      observations: workOrderObservations
                    },
                    { responseType: 'blob' }
                  );
                  
                  // Download PDF
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `Ordem_Trabalho_${client.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                  
                  toast.success("Ordem de Trabalho gerada com sucesso");
                  setShowWorkOrderDialog(false);
                  setWorkOrderPanelsOut([]);
                  setWorkOrderPanelsIn([]);
                  setWorkOrderObservations("");
                  setWorkOrderSearchIn("");
                  await axios.delete(`${API}/clients/${clientId}/work-order-draft`).catch(() => {});
                } catch (error) {
                  console.error("Error generating work order:", error);
                  toast.error("Erro ao gerar Ordem de Trabalho");
                } finally {
                  setGeneratingWorkOrder(false);
                }
              }}
              disabled={generatingWorkOrder}
              data-testid="btn-generate-work-order-pdf"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {generatingWorkOrder ? (
                <>
                  <LoadingSpinner size={16} className="mr-2" />
                  Gerando...
                </>
              ) : (
                <>
                  <FilePdf size={18} className="mr-2" />
                  Gerar PDF
                </>
              )}
            </Button>
            <Button
              onClick={() => {
                setEmailType("work_order");
                setShowEmailDialog(true);
              }}
              variant="outline"
              className="border-[#009392] text-[#009392] hover:bg-[#009392]/10"
            >
              <EnvelopeSimple size={18} className="mr-2" />
              Enviar por Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <EnvelopeSimple size={24} className="text-[#009392]" />
              Enviar por Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="email-recipient">Email do destinatário</Label>
              <Input
                id="email-recipient"
                type="email"
                placeholder="exemplo@email.com"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                data-testid="input-email-recipient"
              />
            </div>
            <div>
              <Label htmlFor="email-message">Mensagem (opcional)</Label>
              <Textarea
                id="email-message"
                placeholder="Adicione uma mensagem personalizada..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={3}
                data-testid="input-email-message"
              />
            </div>
            <div className="bg-stone-50 p-3 rounded-lg">
              <p className="text-sm text-stone-600">
                <strong>Cliente:</strong> {client?.name}
              </p>
              <p className="text-sm text-stone-500 mt-1">
                Será enviado um email com informações do cliente.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEmailDialog(false);
                  setEmailRecipient("");
                  setEmailMessage("");
                }}
                className="flex-1"
                disabled={sendingEmail}
              >
                Cancelar
              </Button>
              <Button
                onClick={sendEmail}
                className="flex-1 bg-[#009392] hover:bg-[#007a7a]"
                disabled={sendingEmail || !emailRecipient}
                data-testid="btn-confirm-send-email"
              >
                {sendingEmail ? (
                  <>Enviando...</>
                ) : (
                  <>
                    <PaperPlaneTilt size={18} className="mr-1" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
